import React, { useState, useEffect, useMemo, useId, useCallback } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  TestTarget,
  ItemCategoryEntry,
  TestTargetType,
  TestTargetFormData,
  ItemDetails,
  ServiceDetails,
  ViewDetails,
  SectionDetails,
  OverlayDetails,
  ReviewStatus,
  InternalProcessFlowEntry,
  ItemTransitionType,
  ItemDisplayTextConditionEntry,
  MaskKind,
  ItemItemCategory,
  ItemSubCategory,
} from "../types/TestTarget";
import { useToast } from "../contexts/ToastContext";
import {
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiConfirmButton,
  IconButton as MuiIconButton,
  TextField,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { STATUS_LABELS } from "../utils/statusUtils";
import { useWorkspaceUserNames } from "../hooks/useWorkspaceUserNames";
import { UserNameDisplay } from "./UserNameDisplay";
import {
  getViewPaths,
  setViewPaths,
  getParamsConditions,
  getViewComponentConditions,
  getConditionsForComponentId,
  upsertComponentCondition,
  removeComponentCondition,
  swapComponentIdPreservingConditions,
  normalizeComponentConditionsForSave,
} from "../utils/viewPathUtils";
import type { ComponentConditionEntry } from "../types/TestTarget";
import {
  ConditionsBlock,
  ConditionsBlockLabel,
  ConditionsReadOnlyList,
  ConditionsReadOnlyItem,
} from "./componentConditionsStyles";
import { FIELD_HELP, FieldHelpButton } from "./help/fieldHelp";
import {
  setServiceBaseUrls,
  serviceBlockToRows,
  rowsToServiceBlock,
  getFirstServiceBaseUrlBlockOrEmpty,
  persistServiceParamsBaseUrl,
  sanitizeBaseUrlBlockForPersistence,
} from "../utils/serviceUtils";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  get_component_ids_from_view_params,
  get_components_under_service,
  resolve_component_by_stored_id,
  unique_trimmed_component_ids,
} from "../utils/viewItemTree";
import { normalizeMaskSettings } from "../utils/maskSettings";
import {
  as_item_details,
  normalize_item_transitions,
} from "../utils/itemParams";
import {
  ITEM_CATEGORY_OPTIONS,
  SUBCATEGORIES_BY_ITEM_CATEGORY,
  resolveItemCategoryAndSubCategory,
  resolveItemCategoryEntries,
  defaultSubCategoryForCategory,
  itemFormShowsChoiceFields,
  itemFormShowsDefaultValueField,
  itemFormShowsCheckboxDefaults,
  itemFormShowsTextConstraints,
} from "../utils/itemCategoryMigration";
import {
  isServiceDeleteNameConfirmed,
  serviceDeleteConfirmationDisplayLabel,
  serviceDeleteConfirmationExpectedValue,
} from "../utils/serviceDeleteConfirm";

/** TestTarget の name に許可される最大文字数（DB の varchar(256) 制約に合わせる） */
const TEST_TARGET_NAME_MAX_LENGTH = 256;

const ITEM_TRANSITION_TYPE_LABELS: Record<ItemTransitionType, string> = {
  same_tab: "同一タブ",
  new_tab: "別タブ",
  new_window: "別ウィンドウ",
};

/** Item の I/O 分類選択肢。編集（ラジオ）と閲覧（Chip）で同じ表記を使うため共通化 */
const ITEM_IO_OPTIONS = [
  { value: "", label: "未指定" },
  { value: "input", label: "input（入力）" },
  { value: "output", label: "output（出力）" },
  { value: "both", label: "both（双方）" },
  { value: "system", label: "system（システム）" },
] as const;

const ITEM_IO_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  ITEM_IO_OPTIONS.map((o) => [o.value, o.label])
);

const ITEM_IS_TEMPLATE_OPTIONS = [
  { value: "", label: "未指定" },
  { value: "true", label: "繰り返し要素である" },
  { value: "false", label: "繰り返し要素ではない" },
] as const;

const isTemplateToRadioValue = (
  v: boolean | undefined
): "" | "true" | "false" => (v === undefined ? "" : v ? "true" : "false");

const radioValueToIsTemplate = (v: string): boolean | undefined =>
  v === "true" ? true : v === "false" ? false : undefined;

function normalizeItemTransitionType(raw: unknown): ItemTransitionType {
  if (raw === "new_tab" || raw === "new_window") return raw;
  return "same_tab";
}

function parse_checkbox_default_values(
  attrs: ItemDetails["htmlAttributes"] | undefined,
  optionCount: number
): boolean[] {
  const raw = attrs?.checkboxDefaultValues;
  const base = Array.isArray(raw)
    ? raw.map((x) => x === true || (typeof x === "string" && x === "true"))
    : [];
  while (base.length < optionCount) {
    base.push(false);
  }
  if (base.length > optionCount) {
    base.length = optionCount;
  }
  return base;
}

/** API の conditions をフォーム用 string[] に正規化（配列要素は1行＝1条件） */
function normalize_item_display_conditions_from_api(raw: unknown): string[] {
  if (raw === undefined || raw === null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x));
  }
  const s = String(raw).trim();
  return s ? [s] : [];
}

/** 保存用: 空行を除き、未入力のみなら undefined */
function build_item_conditions_for_save(rows: string[]): string[] | undefined {
  const out = rows.map((s) => s.trim()).filter((s) => s.length > 0);
  return out.length > 0 ? out : undefined;
}

/** 編集フォーム用: 表示テキスト＋表示条件の 1 行 */
type ItemDisplayTextEntryForm = {
  text: string;
  conditions: string[];
};

/** API / ItemDetails から編集用のセット配列へ（後方互換あり） */
function normalize_item_display_text_entries_for_form(
  itemParams: ItemDetails
): ItemDisplayTextEntryForm[] {
  const fromNew = itemParams.display_text_entries;
  if (Array.isArray(fromNew) && fromNew.length > 0) {
    return fromNew.map((entry) => ({
      text:
        entry.text !== undefined && entry.text !== null
          ? String(entry.text)
          : "",
      conditions: normalize_item_display_conditions_from_api(entry.conditions),
    }));
  }
  const legacyText =
    itemParams.text !== undefined && itemParams.text !== null
      ? String(itemParams.text)
      : "";
  const legacyCond = normalize_item_display_conditions_from_api(
    itemParams.conditions
  );
  if (!legacyText.trim() && legacyCond.length === 0) {
    return [];
  }
  return [{ text: legacyText, conditions: legacyCond }];
}

/** 保存用 display_text_entries（空セットは捨てる） */
function build_item_display_text_entries_for_save(
  rows: ItemDisplayTextEntryForm[]
): ItemDisplayTextConditionEntry[] | undefined {
  const out: ItemDisplayTextConditionEntry[] = [];
  for (const row of rows) {
    const textTrim = row.text.trim();
    const cond = build_item_conditions_for_save(row.conditions);
    const hasText = textTrim.length > 0;
    const hasCond = cond !== undefined && cond.length > 0;
    if (!hasText && !hasCond) {
      continue;
    }
    const piece: ItemDisplayTextConditionEntry = {};
    if (hasText) {
      piece.text = textTrim;
    }
    if (hasCond) {
      piece.conditions = cond;
    }
    out.push(piece);
  }
  return out.length > 0 ? out : undefined;
}

function first_legacy_text_from_saved_display_entries(
  saved: ItemDisplayTextConditionEntry[] | undefined
): string | undefined {
  if (!saved) {
    return undefined;
  }
  const hit = saved.find((e) => e.text && e.text.trim().length > 0);
  return hit?.text?.trim();
}

function legacy_flat_conditions_from_saved_display_entries(
  saved: ItemDisplayTextConditionEntry[] | undefined
): string[] | undefined {
  if (!saved || saved.length !== 1) {
    return undefined;
  }
  return saved[0].conditions;
}

/** 閲覧モード用: 保存形式のセット一覧（空は []） */
function item_params_to_display_view_entries(
  itemParams: ItemDetails
): ItemDisplayTextConditionEntry[] {
  const saved = build_item_display_text_entries_for_save(
    normalize_item_display_text_entries_for_form(itemParams)
  );
  return saved ?? [];
}

/** 編集 UI 用: 空なら 1 行の空入力を出す */
function normalize_component_ids_for_edit(ids: string[]): string[] {
  return ids.length > 0 ? ids : [""];
}

// アニメーション
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// ベースコンポーネント
const Overlay = styled.div<{
  open: boolean;
  zIndex?: number;
  insetTopPx?: number;
}>`
  position: fixed;
  top: ${(props) => (props.insetTopPx != null ? `${props.insetTopPx}px` : "0")};
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: ${(props) => (props.open ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: ${(props) => props.zIndex || 1400};
  animation: ${fadeIn} 0.2s ease-out;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  max-width: 1000px;
  width: 95%;
  height: 85vh;
  overflow: hidden;
  animation: ${slideIn} 0.2s ease-out;
  margin: 24px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
`;

const TypeBadge = styled.span<{ type: string }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.type) {
      case "service":
        return "#dbeafe";
      case "view":
        return "#dcfce7";
      case "component":
        return "#e0e7ff";
      case "section":
        return "#fef3c7";
      case "overlay":
        return "#ede9fe";
      case "item":
        return "#fce7f3";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${(props) => {
    switch (props.type) {
      case "service":
        return "#1e40af";
      case "view":
        return "#166534";
      case "component":
        return "#3730a3";
      case "section":
        return "#92400e";
      case "overlay":
        return "#7c2d12";
      case "item":
        return "#be185d";
      default:
        return "#374151";
    }
  }};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  padding: 6px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

// メインタブ用のコンポーネント
const MainTabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${(props) => (props.$active ? "white" : "transparent")};
  color: ${(props) => (props.$active ? "#1f2937" : "#6b7280")};
  font-weight: ${(props) => (props.$active ? "600" : "400")};
  font-size: 0.875rem;
  cursor: pointer;
  border-bottom: 2px solid ${(props) =>
    props.$active ? "#3b82f6" : "transparent"};
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.$active ? "white" : "#f3f4f6")};
    color: #374151;
  }
`;

const TabContent = styled.div`
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const Grid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns || 2}, 1fr);
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div<{ $fullWidth?: boolean }>`
  grid-column: ${(props) => (props.$fullWidth ? "1 / -1" : "auto")};
`;

/** Grid 外に単独で置く項目用（Grid の margin-bottom と同じ 16px で縦リズムを揃える） */
const StandaloneField = styled(Field)`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
`;

const RequiredMark = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
`;

const RadioRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 4px;
`;

const RadioOptionLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
`;

const RadioInput = styled.input.attrs({ type: "radio" })`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const CheckboxDefaultRow = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div`
  font-weight: 500;
  color: #6b7280;
  font-size: 0.875rem;
  width: 120px;
  flex-shrink: 0;
`;

const InfoValue = styled.div`
  color: #1f2937;
  font-size: 0.875rem;
  flex: 1;
  word-break: break-word;
  white-space: pre-wrap;
`;

const CodeValue = styled.code`
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 0.8rem;
  color: #1f2937;
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Chip = styled.span<{
  variant?: "primary" | "secondary" | "success" | "warning" | "error";
}>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.variant) {
      case "primary":
        return "#dbeafe";
      case "success":
        return "#dcfce7";
      case "warning":
        return "#fef3c7";
      case "error":
        return "#fee2e2";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${(props) => {
    switch (props.variant) {
      case "primary":
        return "#1e40af";
      case "success":
        return "#166534";
      case "warning":
        return "#92400e";
      case "error":
        return "#dc2626";
      default:
        return "#374151";
    }
  }};
`;

const TransitionInfo = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px;
  margin-top: 8px;
`;

const NotAvailableMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
`;

const TransitionHeader = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TransitionTarget = styled.span`
  font-weight: 500;
`;

const TransitionDetail = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SelectorList = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px;
`;

const SelectorItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SelectorKey = styled.span`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const SelectorValue = styled.code`
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 0.8rem;
  color: #1f2937;
  flex: 1;
  margin-left: 8px;
  word-break: break-all;
`;

const KeyValueEditor = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
`;

const DisplayTextSetCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background: #fafbfc;
`;

const DisplayTextSetCardTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 10px;
`;

const KeyValueItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const KeyValueInput = styled.input`
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const AddButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #f9fafb;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  margin: 8px;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const RemoveButton = styled.button`
  padding: 4px 6px;
  border: 1px solid #fca5a5;
  border-radius: 4px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 0.75rem;
  cursor: pointer;

  &:hover {
    background: #fee2e2;
  }
`;

const PathInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  & > input,
  & > select {
    flex: 1;
    min-width: 0;
  }
`;

const PathInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PathSelect = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ComponentErrorText = styled.div`
  font-size: 0.8rem;
  font-weight: 500;
  color: #b91c1c;
  margin-top: 2px;
  margin-bottom: 4px;
`;

const PathList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PathItem = styled.div`
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1f2937;
  border: 1px solid #e2e8f0;
`;

const ComponentOutlineAddButton = styled.button`
  margin-top: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #667eea;
  background: transparent;
  color: #667eea;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #f1f5f9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PathListBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  width: 100%;
  box-sizing: border-box;
`;

const PathListItem = styled.div`
  padding: 6px 10px;
  background: #ffffff;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #1f2937;
  border: 1px solid #e5e7eb;
  word-break: break-all;
`;

const BaseUrlBlock = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const BaseUrlKvRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  & > input:nth-of-type(1) {
    flex: 0 0 140px;
  }

  & > input:nth-of-type(2) {
    flex: 1;
    min-width: 0;
  }
`;

const BaseUrlBlockActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

// タブ関連のスタイルコンポーネント
const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const TabList = styled.div`
  display: flex;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  min-height: 42px;
  align-items: center;
  padding: 0 8px;
  gap: 4px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${(props) => (props.$active ? "#ffffff" : "transparent")};
  color: ${(props) => (props.$active ? "#3b82f6" : "#6b7280")};
  font-size: 0.875rem;
  font-weight: ${(props) => (props.$active ? "600" : "400")};
  cursor: pointer;
  border-radius: 4px 4px 0 0;
  white-space: nowrap;
  transition: all 0.2s;
  position: relative;
  display: flex;
  align-items: center;

  ${(props) =>
    props.$active &&
    `
    box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.05);
    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background: #3b82f6;
    }
  `}

  &:hover:not(:disabled) {
    background: ${(props) => (props.$active ? "#ffffff" : "#e5e7eb")};
    color: ${(props) => (props.$active ? "#3b82f6" : "#374151")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TabPanel = styled.div`
  padding: 16px;
  background: white;
`;

const AddTabButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #6b7280;
  font-size: 0.825rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  margin-left: auto;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #374151;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TabCloseButton = styled.button`
  padding: 2px;
  margin-left: 8px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  border-radius: 2px;

  &:hover {
    background: #fee2e2;
    color: #dc2626;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CompactTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  margin-top: 8px;
`;

const CompactTableHeader = styled.th`
  background-color: #f8f9fa;
  border: 1px solid #e5e7eb;
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 0.75rem;
`;

const CompactTableCell = styled.td`
  border: 1px solid #e5e7eb;
  padding: 6px 12px;
  vertical-align: top;
  color: #374151;
  font-size: 0.75rem;
`;

const CompactTableRow = styled.tr`
  &:hover {
    background-color: #f9fafb;
  }
`;

const Footer = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ValidationErrorContainer = styled.div`
  padding: 8px 12px;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
`;

const ValidationErrorList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
`;

const ChangeWarningText = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  color: #d97706;
  font-size: 0.875rem;
  font-weight: 500;
  margin-right: 8px;
`;

const Button = styled.button<{ variant?: "primary" | "secondary" | "danger" }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid;
  
  ${(props) => {
    if (props.variant === "primary") {
      return `
        background: #3b82f6;
        color: white;
        border-color: #3b82f6;
        
        &:hover {
          background: #2563eb;
          border-color: #2563eb;
        }
      `;
    } else if (props.variant === "danger") {
      return `
        background: #ef4444;
        color: #ffffff;
        border-color: #ef4444;
        
        &:hover:not(:disabled) {
          background: #e92929;
          border-color: #e92929;
        }
        
        &:disabled {
          background: #fecdd3;
          color: #ffffff;
          cursor: not-allowed;
        }
      `;
    } else {
      return `
        background: white;
        color: #374151;
        border-color: #d1d5db;
        
        &:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }
      `;
    }
  }}
`;

// アイコンコンポーネント
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const SaveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z" />
  </svg>
);

const CancelIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
  </svg>
);

// 新しいスタイルコンポーネントを追加
const SectionTitle = styled.h3`
  margin: 16px 0 8px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 4px;
`;

const BehaviorSection = styled.div`
  margin-bottom: 28px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const BehaviorSubsectionTitle = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
`;

const BehaviorHint = styled.p`
  margin: 0 0 12px 0;
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
`;

const FlowTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FlowStepCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
  border-left: 3px solid #3b82f6;
`;

const FlowParallelMarker = styled.div<{ $variant: "start" | "end" }>`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  background: ${(p) => (p.$variant === "start" ? "#ede9fe" : "#e0e7ff")};
  color: #5b21b6;
  border: 1px solid #c4b5fd;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const FlowStepMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.75rem;
  color: #6b7280;
`;

const FlowActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const INTERNAL_PROCESS_CONTENT_PLACEHOLDER =
  "計算を含む処理の場合は、式・丸め・前提条件など計算ロジックを必ず記載してください。計算以外の場合は、実行される処理の内容を記載してください。";

function newInternalFlowEntryId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `flow-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function createDefaultInternalProcessStep(): InternalProcessFlowEntry {
  return {
    id: newInternalFlowEntryId(),
    kind: "step",
    processName: "",
    processContent: "",
    expectedChangeOnSuccess: "",
    expectedChangeOnError: "",
    isAsync: false,
    precondition: "",
  };
}

function parseInternalProcessFlow(raw: unknown): InternalProcessFlowEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((entry, idx) => {
    if (!entry || typeof entry !== "object") {
      return createDefaultInternalProcessStep();
    }
    const o = entry as Record<string, unknown>;
    const id = String(o.id ?? `flow-${idx}`);
    if (o.kind === "parallel_start") {
      return { id, kind: "parallel_start" };
    }
    if (o.kind === "parallel_end") {
      return { id, kind: "parallel_end" };
    }
    const legacyExpected = String(o.expectedChange ?? o.expected_change ?? "");
    const expectedOnSuccess = String(
      o.expectedChangeOnSuccess ??
        o.expected_change_on_success ??
        legacyExpected
    );
    const expectedOnError = String(
      o.expectedChangeOnError ?? o.expected_change_on_error ?? ""
    );
    return {
      id,
      kind: "step",
      processName: String(o.processName ?? o.process_name ?? ""),
      processContent: String(o.processContent ?? o.process_content ?? ""),
      expectedChangeOnSuccess: expectedOnSuccess,
      expectedChangeOnError: expectedOnError,
      isAsync: Boolean(o.isAsync ?? o.is_async),
      precondition: String(o.precondition ?? ""),
    };
  });
}

const OptionGroup = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
`;

const OptionGroupTitle = styled.div`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
  margin-bottom: 8px;
`;

// 親要素選択用のスタイルコンポーネント
const ParentSelectionContainer = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 12px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ParentSelectionField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ParentUnavailableMessage = styled.div`
  padding: 8px 0;
  color: #6b7280;
  font-size: 0.875rem;
`;

// ステータスバッジ
const StatusBadge = styled.span<{ status: ReviewStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  gap: 4px;

  ${(props) => {
    switch (props.status) {
      case "draft":
        return `
          background: #f3f4f6;
          color: #6b7280;
        `;
      case "need_review":
        return `
          background: #fef3c7;
          color: #d97706;
          border: 1px solid #fbbf24;
        `;
      case "in_review":
        return `
          background: #dbeafe;
          color: #2563eb;
          border: 1px solid #60a5fa;
        `;
      case "approved":
        return `
          background: #d1fae5;
          color: #059669;
          border: 1px solid #34d399;
        `;
      case "rejected":
        return `
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #f87171;
        `;
      case "archived":
        return `
          background: #e5e7eb;
          color: #4b5563;
        `;
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
        `;
    }
  }}
`;

interface TestTargetDetailViewProps {
  open: boolean;
  onClose: () => void;
  onSave: (testTarget: TestTargetFormData) => Promise<void>;
  testTarget?: TestTarget | null;
  testTargets?: TestTarget[];
  initialMode?: "view" | "edit" | "create";
  initialTab?: string;
  apiClient?: any;
  workspaceId?: string;
  projectId?: string;
  testcaseMappings?: import("../services/api").TestcaseMappings | null;
  zIndex?: number;
  /** オーバーレイ開始位置（上端）を調整したい場合に指定 */
  backdropInsetTopPx?: number;
  hideEditButton?: boolean;
  /** サービス詳細の閲覧モードから削除（API 呼び出しは呼び出し側） */
  onDelete?: () => Promise<void>;
}

const testTargetTypes: { value: TestTargetType; label: string }[] = [
  { value: "service", label: "サービス" },
  { value: "category", label: "カテゴリー" },
  { value: "view", label: "画面" },
  { value: "component", label: "コンポーネント" },
  { value: "section", label: "セクション" },
  { value: "overlay", label: "オーバーレイ" },
  { value: "item", label: "項目" },
  { value: "component-ref", label: "コンポーネント参照" },
];

const DEFAULT_DISPLAY_ORDER = 1;

const TestTargetDetailView: React.FC<TestTargetDetailViewProps> = ({
  open,
  onClose,
  onSave,
  testTarget,
  testTargets = [],
  initialMode = "view",
  initialTab = "basic",
  apiClient,
  workspaceId,
  projectId,
  testcaseMappings,
  zIndex,
  backdropInsetTopPx,
  hideEditButton = false,
  onDelete,
}) => {
  const { showSuccess, showError } = useToast();
  const [isEditMode, setIsEditMode] = useState(
    initialMode === "edit" || initialMode === "create"
  );
  const [isSaving, setIsSaving] = useState(false);
  const [serviceDeleteDialogOpen, setServiceDeleteDialogOpen] = useState(false);
  const [serviceDeleteConfirmInput, setServiceDeleteConfirmInput] =
    useState("");
  const [isDeletingService, setIsDeletingService] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (serviceDeleteDialogOpen) {
      setServiceDeleteConfirmInput("");
    }
  }, [serviceDeleteDialogOpen]);

  // マスク設定 state
  const [maskType, setMaskType] = useState<MaskKind | "">("");

  // 遷移先のIDから名前を取得する関数
  const getTransitionTargetName = (targetId: string): string => {
    const target = testTargets.find((t: TestTarget) => t.id === targetId);
    return target ? target.name : targetId; // 見つからない場合はIDをそのまま表示
  };

  // 指定したIDからルートサービスのIDを取得する関数
  const findRootService = (targetId: string): string | null => {
    if (!targetId) return null;

    const target = testTargets.find((t) => t.id === targetId);
    if (!target) return null;

    // 自分がサービスの場合はそのIDを返す
    if (target.type === "service") {
      return target.id;
    }

    // 親が存在する場合は再帰的に検索
    if (target.parentId) {
      return findRootService(target.parentId);
    }

    return null;
  };

  // カテゴリーの階層パスラベル（"親カテゴリー / 子カテゴリー"）を組み立てる
  const categoryPathLabel = (categoryId: string): string => {
    const names: string[] = [];
    const seen = new Set<string>();
    let cur = testTargets.find((t) => t.id === categoryId);
    while (cur && cur.type === "category" && !seen.has(cur.id)) {
      seen.add(cur.id);
      names.unshift(String(cur.name));
      cur = cur.parentId
        ? testTargets.find((t) => t.id === cur?.parentId)
        : undefined;
    }
    return names.join(" / ");
  };

  // candidateId が ancestorId 自身、またはその子孫かどうか（カテゴリーの循環参照防止用）
  const isSelfOrDescendant = (
    candidateId: string,
    ancestorId: string | undefined
  ): boolean => {
    if (!ancestorId) return false;
    if (candidateId === ancestorId) return true;
    const seen = new Set<string>();
    let cur = testTargets.find((t) => t.id === candidateId);
    while (cur && cur.parentId && !seen.has(cur.id)) {
      seen.add(cur.id);
      if (cur.parentId === ancestorId) return true;
      cur = testTargets.find((t) => t.id === cur?.parentId);
    }
    return false;
  };

  const metaUserIds = useMemo(
    () =>
      testTarget?.metaInfo
        ? [testTarget.metaInfo.createdBy, testTarget.metaInfo.modifiedBy]
        : [],
    [testTarget?.metaInfo?.createdBy, testTarget?.metaInfo?.modifiedBy]
  );
  const { names: userNames } = useWorkspaceUserNames(
    apiClient,
    workspaceId,
    metaUserIds
  );

  // 指定したIDから親ViewのIDを取得する関数
  const findParentView = (targetId: string): string | null => {
    if (!targetId) return null;

    const target = testTargets.find((t) => t.id === targetId);
    if (!target) return null;

    // 自分がViewの場合はそのIDを返す
    if (target.type === "view") {
      return target.id;
    }

    // 親が存在する場合は再帰的に検索
    if (target.parentId) {
      return findParentView(target.parentId);
    }

    return null;
  };

  /** View または Component（Service 直下の画面コンテキスト）を祖先方向に探索 */
  const findParentViewOrComponent = (targetId: string): string | null => {
    if (!targetId) return null;

    const target = testTargets.find((t) => t.id === targetId);
    if (!target) return null;

    if (target.type === "view" || target.type === "component") {
      return target.id;
    }

    if (target.parentId) {
      return findParentViewOrComponent(target.parentId);
    }

    return null;
  };

  // parentIdから各階層のIDを逆算する関数
  const parseParentHierarchy = (parentId: string | undefined): {
    serviceId: string | null;
    viewId: string | null;
    itemId: string | null;
  } => {
    if (!parentId) {
      return { serviceId: null, viewId: null, itemId: null };
    }

    const target = testTargets.find((t) => t.id === parentId);
    if (!target) {
      return { serviceId: null, viewId: null, itemId: null };
    }

    const serviceId = findRootService(parentId);
    const viewId = findParentViewOrComponent(parentId);

    // アイテム（service / view / component / category 以外が親のときは Item 階層としてその ID）
    // category は View をまとめるコンテナなので Item 階層には含めない。
    const itemId =
      target.type !== "service" &&
      target.type !== "view" &&
      target.type !== "component" &&
      target.type !== "category"
        ? parentId
        : null;

    return { serviceId, viewId, itemId };
  };

  // 親要素選択の状態管理
  const [selectedParentServiceId, setSelectedParentServiceId] = useState<string | null>(null);
  const [selectedParentViewId, setSelectedParentViewId] = useState<string | null>(null);
  const [selectedParentItemId, setSelectedParentItemId] = useState<string | null>(null);

  const [editedData, setEditedData] = useState<TestTargetFormData>({
    name: "",
    type: "service",
    description: "",
    displayOrder: DEFAULT_DISPLAY_ORDER,
    parentId: undefined,
    status: "draft",
    params: {},
  });

  const build_item_details_initial_state = () => ({
    displayTextEntries: [] as ItemDisplayTextEntryForm[],
    selector: "",
    itemCategory: "Action" as ItemItemCategory,
    itemSubCategory: "button" as ItemSubCategory | undefined,
    itemCategoryEntries: [
      {
        itemCategory: "Action" as ItemItemCategory,
        itemSubCategory: "button" as ItemSubCategory | undefined,
      },
    ] as ItemCategoryEntry[],
    required: false,
    maxLength: undefined as number | undefined,
    minLength: undefined as number | undefined,
    pattern: "",
    placeholder: "",
    defaultValue: "",
    checkboxDefaultValues: [] as boolean[],
    transitions: [] as Array<{
      targetId: string;
      condition: string;
      description: string;
      status?: ReviewStatus;
      transitionType: ItemTransitionType;
    }>,
    internalProcessFlow: [] as InternalProcessFlowEntry[],
    options: [] as string[],
    isDownload: false,
    customTypeFlg: false,
    isTemplate: undefined as boolean | undefined,
    formCategory: undefined as string | undefined,
    io: "" as "" | "input" | "output" | "both" | "system",
    dbMapping: [] as { table: string; column: string }[],
  });
  const build_execution_control_initial_state = () => ({
    isManual: false,
    forceAnalyze: false,
    failCount: 0,
  });
  const build_options_initial_state = () => ({
    checkbox: {
      onStateSelector: "",
    },
  });

  // 項目詳細のフォーム状態
  const [itemDetails, setItemDetails] = useState(build_item_details_initial_state);

  const itemDefaultValueDatalistId = useId();

  const [transitionServiceSelections, setTransitionServiceSelections] =
    useState<string[]>([]);

  const getOwnerServiceId = (): string =>
    findRootService(testTarget?.id || editedData.parentId || "") || "";

  const view_component_picker_service_id = useMemo(() => {
    if (editedData.type !== "view") {
      return null;
    }
    if (selectedParentServiceId) {
      return selectedParentServiceId;
    }
    const from_existing =
      (testTarget?.id && findRootService(testTarget.id)) ||
      (editedData.parentId && findRootService(editedData.parentId)) ||
      null;
    return from_existing;
  }, [
    editedData.type,
    editedData.parentId,
    selectedParentServiceId,
    testTarget?.id,
    testTargets,
  ]);

  const available_view_components = useMemo(() => {
    if (!view_component_picker_service_id) {
      return [] as { id: string; name: string }[];
    }
    return get_components_under_service(
      view_component_picker_service_id,
      testTargets
    )
      .slice()
      .sort(
        (a, b) =>
          (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
          a.name.localeCompare(b.name)
      )
      .map((t) => ({ id: t.id, name: t.name }));
  }, [view_component_picker_service_id, testTargets]);

  // セレクターのKey:Valueペア管理
  const [selectorPairs, setSelectorPairs] = useState<
    Array<{ key: string; value: string }>
  >([]);

  // 実行制御パラメータの状態を追加
  const [executionControl, setExecutionControl] = useState(
    build_execution_control_initial_state
  );

  // 実行オプションの状態を追加
  const [options, setOptions] = useState(build_options_initial_state);

  // アクティブな遷移タブの状態
  const [activeTransitionTab, setActiveTransitionTab] = useState(0);

  const reset_item_related_form_state = useCallback(() => {
    setItemDetails(build_item_details_initial_state());
    setTransitionServiceSelections([]);
    setSelectorPairs([{ key: "", value: "" }]);
    setExecutionControl(build_execution_control_initial_state());
    setOptions(build_options_initial_state());
    setActiveTransitionTab(0);
  }, []);

  const serviceTargets = testTargets.filter((target) => target.type === "service");
  const activeTransitionServiceId =
    transitionServiceSelections[activeTransitionTab] || "";
  const availableViewsForActiveTransition = activeTransitionServiceId
    ? testTargets.filter((target) => {
        if (target.type !== "view") return false;
        if (initialMode !== "create" && target.id === testTarget?.id) {
          return false;
        }
        const targetService = findRootService(target.id);
        return (
          targetService && targetService === activeTransitionServiceId
        );
      })
    : [];

  // 関連テストケースの状態
  const [relatedTestCases, setRelatedTestCases] = useState<
    Array<{
      testCaseId: string;
      testCaseName: string;
      testSuiteId: string;
      testSuiteName: string;
      metaId: string;
      stepIndex?: number;
      stepDescription?: string;
      stepAction?: string;
    }>
  >([]);
  const [isLoadingTestCases, setIsLoadingTestCases] = useState(false);
  const [testCasesError, setTestCasesError] = useState<string | null>(null);

  // 編集前の元の状態を保存するための状態変数
  const [originalTestTargetData, setOriginalTestTargetData] = useState<{
    editedData: TestTargetFormData;
    itemDetails: any;
    selectorPairs: Array<{ key: string; value: string }>;
    executionControl: any;
    options: any;
    transitionServiceSelections: string[];
    selectedParentServiceId: string | null;
    selectedParentViewId: string | null;
    selectedParentItemId: string | null;
    maskType: MaskKind | "";
  } | null>(null);

  // ESCキーでダイアログを閉じる（編集モード時は無効）
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open && !isEditMode) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscKey, { capture: true });
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey, { capture: true });
    };
  }, [open, onClose, isEditMode]);

  useEffect(() => {
    // initialModeが変更された時に編集モードを更新（ダイアログが開いているときのみ）
    if (open) {
      setIsEditMode(initialMode === "edit" || initialMode === "create");
    }

    if (testTarget) {
      // statusを取得: トップレベルのstatusを優先、なければparams.status、それもなければcreate modeの場合は"draft"
      let status: ReviewStatus | undefined;
      
      // まずトップレベルのstatusをチェック（formDataから来た場合）
      if ('status' in testTarget && testTarget.status) {
        status = testTarget.status as ReviewStatus;
      }
      // 次にparams.statusをチェック
      else if (
        testTarget.params &&
        typeof testTarget.params === "object" &&
        "status" in testTarget.params
      ) {
        const paramsStatus = testTarget.params.status as ReviewStatus;
        // NOTE: params.statusがnullの場合は"draft"をデフォルト値として設定
        status = paramsStatus || "draft";
      }
      // create modeでstatusがない場合は"draft"をデフォルト
      else if (initialMode === "create") {
        status = "draft";
      }
      // 既存ターゲットでstatusがnullの場合は"draft"をデフォルト
      else {
        status = "draft";
      }

      setEditedData({
        name: String(testTarget.name || ""),
        type: testTarget.type,
        description: String(testTarget.description || ""),
        displayOrder: testTarget.displayOrder,
        parentId: testTarget.parentId,
        status: status,
        params: testTarget.params,
      });

      // 親要素の階層を初期化
      const hierarchy = parseParentHierarchy(testTarget.parentId);
      setSelectedParentServiceId(hierarchy.serviceId);
      setSelectedParentViewId(hierarchy.viewId);
      setSelectedParentItemId(hierarchy.itemId);

      // NOTE: selector 無し Item（CSV インポート等）でも transitions 等をフォームへ載せる
      const itemParams = as_item_details(testTarget.params);
      if (testTarget.type === "item" && itemParams) {
        const transitions = normalize_item_transitions(itemParams);

        const mappedTransitions = transitions.map((t) => ({
          targetId: String(t.targetId || ""),
          condition: String(t.condition || ""),
          description: String(t.description || ""),
          status: t.status as ReviewStatus | undefined,
          transitionType: normalizeItemTransitionType(
            t.transitionType ??
              (t as { transition_type?: unknown }).transition_type
          ),
        }));

        const loadedItemOptions = Array.isArray(itemParams.available_choices)
          ? itemParams.available_choices.map((opt) =>
              typeof opt === "string" ? opt : opt.value || opt.label || ""
            )
          : [];

        const resolvedEntries = resolveItemCategoryEntries(itemParams);
        const resolved = resolvedEntries[0] ?? resolveItemCategoryAndSubCategory(itemParams);
        setItemDetails({
          displayTextEntries: normalize_item_display_text_entries_for_form(
            itemParams
          ),
          selector:
            typeof itemParams.selector === "object"
              ? JSON.stringify(itemParams.selector, null, 2)
              : String(itemParams.selector || ""),
          itemCategory: resolved.itemCategory,
          itemSubCategory: resolved.itemSubCategory,
          itemCategoryEntries: resolvedEntries,
          required: itemParams.htmlAttributes?.required || false,
          maxLength: itemParams.htmlAttributes?.maxLength,
          minLength: itemParams.htmlAttributes?.minLength,
          pattern: String(itemParams.htmlAttributes?.pattern || ""),
          placeholder: String(itemParams.htmlAttributes?.placeholder || ""),
          defaultValue: String(itemParams.htmlAttributes?.defaultValue || ""),
          transitions: mappedTransitions,
          internalProcessFlow: parseInternalProcessFlow(
            itemParams.internalProcessFlow ??
              (itemParams as { internal_process_flow?: unknown })
                .internal_process_flow
          ),
          options: loadedItemOptions,
          checkboxDefaultValues: parse_checkbox_default_values(
            itemParams.htmlAttributes,
            loadedItemOptions.length
          ),
          isDownload: itemParams.isDownload || false,
          customTypeFlg: itemParams.customTypeFlg || false,
          isTemplate:
            typeof itemParams.isTemplate === "boolean"
              ? itemParams.isTemplate
              : undefined,
          formCategory: itemParams.formCategory ?? undefined,
          io: itemParams.io ?? "",
          dbMapping: Array.isArray(itemParams.dbMapping)
            ? itemParams.dbMapping.map((m) => ({
                table: String(m?.table ?? ""),
                column: String(m?.column ?? ""),
              }))
            : [],
        });

        setTransitionServiceSelections(
          mappedTransitions.map((transition) => {
            const transitionService = transition.targetId
              ? findRootService(transition.targetId)
              : null;
            return transitionService || getOwnerServiceId();
          })
        );
        setActiveTransitionTab(0);

        // セレクターオブジェクトをKey:Valueペアに変換
        if (
          typeof itemParams.selector === "object" &&
          itemParams.selector !== null
        ) {
          const pairs = Object.entries(itemParams.selector).map(
            ([key, value]) => ({
              key,
              value: String(value),
            })
          );
          setSelectorPairs(pairs);
        } else {
          setSelectorPairs([
            { key: "default", value: String(itemParams.selector || "") },
          ]);
        }

        // 実行制御パラメータを設定
        setExecutionControl({
          isManual: itemParams.isManual || false,
          forceAnalyze: itemParams.forceAnalyze || false,
          failCount: itemParams.failCount || 0,
        });

        // 実行オプションを設定
        setOptions({
          checkbox: {
            onStateSelector:
              itemParams.options?.checkbox?.onStateSelector || "",
          },
        });
      } else {
        reset_item_related_form_state();
      }
      // マスク設定を testTarget.params から復元（item/section/overlay 共通）
      const restoreParams = testTarget.params as
        | { maskType?: MaskKind }
        | undefined;
      setMaskType(restoreParams?.maskType ?? "");
    } else if (initialMode === "create") {
      setEditedData({
        name: "",
        type: "service",
        description: "",
        displayOrder: DEFAULT_DISPLAY_ORDER,
        parentId: undefined,
        status: "draft",
        params: {},
      });
      // 親要素の階層をリセット
      setSelectedParentServiceId(null);
      setSelectedParentViewId(null);
      setSelectedParentItemId(null);
      reset_item_related_form_state();
      // マスク設定 state を初期化
      setMaskType("");
    }
  }, [testTarget, initialMode, open, reset_item_related_form_state]);

  // ダイアログが開かれたときにinitialTabに基づいてactiveTabをリセットし、isEditModeをリセット
  // ダイアログが開かれたとき、またはtestTargetが変更されたときにoriginalTestTargetDataをリセット
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      setIsEditMode(initialMode === "edit" || initialMode === "create");
      // 新しいターゲットが開かれたときにoriginalTestTargetDataをリセット
      setOriginalTestTargetData(null);
    }
  }, [open, initialTab, initialMode, testTarget?.id]);

  // モーダルを閉じた時にバリデーションエラーメッセージをクリアし、originalTestTargetDataをリセット
  useEffect(() => {
    if (!open) {
      setValidationErrors([]);
      setOriginalTestTargetData(null);
      reset_item_related_form_state();
      setServiceDeleteDialogOpen(false);
      setIsDeletingService(false);
    }
  }, [open, reset_item_related_form_state]);

  // 編集前の元の状態を保存（testTargetが読み込まれ、itemDetailsなどが設定された後に一度だけ実行）
  useEffect(() => {
    if (testTarget && !originalTestTargetData && editedData.name) {
      // params.statusをトップレベルのstatusとして取り出す
      const status =
        testTarget.params &&
        typeof testTarget.params === "object" &&
        "status" in testTarget.params
          ? (testTarget.params.status as ReviewStatus)
          : undefined;

      // item / 非 item とも、読み込み後のフォーム状態を保存する（selector 有無は問わない）
      setOriginalTestTargetData({
        editedData: JSON.parse(
          JSON.stringify({
            name: String(testTarget.name || ""),
            type: testTarget.type,
            description: String(testTarget.description || ""),
            displayOrder: testTarget.displayOrder,
            parentId: testTarget.parentId,
            status: status,
            params: testTarget.params,
          })
        ),
        itemDetails: JSON.parse(JSON.stringify(itemDetails)),
        selectorPairs: JSON.parse(JSON.stringify(selectorPairs)),
        executionControl: JSON.parse(JSON.stringify(executionControl)),
        options: JSON.parse(JSON.stringify(options)),
        transitionServiceSelections: JSON.parse(
          JSON.stringify(transitionServiceSelections)
        ),
        selectedParentServiceId,
        selectedParentViewId,
        selectedParentItemId,
        maskType,
      });
    }
  }, [
    testTarget,
    editedData.name,
    itemDetails,
    selectorPairs,
    executionControl,
    options,
    originalTestTargetData,
    transitionServiceSelections,
  ]);

  // testTargetプロパティが外部から更新された時に最新データを再ロード
  useEffect(() => {
    if (!isEditMode && testTarget) {
      console.log(
        "🔄 TestTargetDetailView: 参照モードでtestTarget更新",
        testTarget
      );
      // 参照モードで外部からtestTargetが更新された際に、最新のtestTargetデータで更新
      // statusを取得: トップレベルのstatusを優先、なければparams.status
      let status: ReviewStatus | undefined;
      
      // まずトップレベルのstatusをチェック（formDataから来た場合）
      if ('status' in testTarget && testTarget.status) {
        status = testTarget.status as ReviewStatus;
      }
      // 次にparams.statusをチェック
      else if (
        testTarget.params &&
        typeof testTarget.params === "object" &&
        "status" in testTarget.params
      ) {
        status = testTarget.params.status as ReviewStatus;
      }

      setEditedData({
        name: String(testTarget.name || ""),
        type: testTarget.type,
        description: String(testTarget.description || ""),
        displayOrder: testTarget.displayOrder,
        parentId: testTarget.parentId,
        status: status,
        params: testTarget.params,
      });

      // 親要素の階層を初期化
      const hierarchy = parseParentHierarchy(testTarget.parentId);
      setSelectedParentServiceId(hierarchy.serviceId);
      setSelectedParentViewId(hierarchy.viewId);
      setSelectedParentItemId(hierarchy.itemId);

      // NOTE: selector 無し Item（CSV インポート等）でも transitions 等をフォームへ載せる
      const itemParams = as_item_details(testTarget.params);
      if (testTarget.type === "item" && itemParams) {
        const transitions = normalize_item_transitions(itemParams);

        const mappedTransitions = transitions.map((t) => ({
          targetId: String(t.targetId || ""),
          condition: String(t.condition || ""),
          description: String(t.description || ""),
          status: t.status as ReviewStatus | undefined,
          transitionType: normalizeItemTransitionType(
            t.transitionType ??
              (t as { transition_type?: unknown }).transition_type
          ),
        }));

        const loadedItemOptionsRef = Array.isArray(itemParams.available_choices)
          ? itemParams.available_choices.map((opt) =>
              typeof opt === "string" ? opt : opt.value || opt.label || ""
            )
          : [];

        const resolvedEntries = resolveItemCategoryEntries(itemParams);
        const resolved = resolvedEntries[0] ?? resolveItemCategoryAndSubCategory(itemParams);
        setItemDetails({
          displayTextEntries: normalize_item_display_text_entries_for_form(
            itemParams
          ),
          selector:
            typeof itemParams.selector === "object"
              ? JSON.stringify(itemParams.selector, null, 2)
              : String(itemParams.selector || ""),
          itemCategory: resolved.itemCategory,
          itemSubCategory: resolved.itemSubCategory,
          itemCategoryEntries: resolvedEntries,
          required: itemParams.htmlAttributes?.required || false,
          maxLength: itemParams.htmlAttributes?.maxLength,
          minLength: itemParams.htmlAttributes?.minLength,
          pattern: String(itemParams.htmlAttributes?.pattern || ""),
          placeholder: String(itemParams.htmlAttributes?.placeholder || ""),
          defaultValue: String(itemParams.htmlAttributes?.defaultValue || ""),
          transitions: mappedTransitions,
          internalProcessFlow: parseInternalProcessFlow(
            itemParams.internalProcessFlow ??
              (itemParams as { internal_process_flow?: unknown })
                .internal_process_flow
          ),
          options: loadedItemOptionsRef,
          checkboxDefaultValues: parse_checkbox_default_values(
            itemParams.htmlAttributes,
            loadedItemOptionsRef.length
          ),
          isDownload: itemParams.isDownload || false,
          customTypeFlg: itemParams.customTypeFlg || false,
          isTemplate:
            typeof itemParams.isTemplate === "boolean"
              ? itemParams.isTemplate
              : undefined,
          formCategory: itemParams.formCategory ?? undefined,
          io: itemParams.io ?? "",
          dbMapping: Array.isArray(itemParams.dbMapping)
            ? itemParams.dbMapping.map((m) => ({
                table: String(m?.table ?? ""),
                column: String(m?.column ?? ""),
              }))
            : [],
        });

        setTransitionServiceSelections(
          mappedTransitions.map((transition) => {
            const transitionService = transition.targetId
              ? findRootService(transition.targetId)
              : null;
            return transitionService || getOwnerServiceId();
          })
        );
        setActiveTransitionTab(0);

        // セレクターオブジェクトをKey:Valueペアに変換
        if (
          typeof itemParams.selector === "object" &&
          itemParams.selector !== null
        ) {
          const pairs = Object.entries(itemParams.selector).map(
            ([key, value]) => ({
              key,
              value: String(value),
            })
          );
          setSelectorPairs(pairs);
        } else {
          setSelectorPairs([
            { key: "default", value: String(itemParams.selector || "") },
          ]);
        }

        // 実行制御パラメータを設定
        setExecutionControl({
          isManual: itemParams.isManual || false,
          forceAnalyze: itemParams.forceAnalyze || false,
          failCount: itemParams.failCount || 0,
        });

        // 実行オプションを設定
        setOptions({
          checkbox: {
            onStateSelector:
              itemParams.options?.checkbox?.onStateSelector || "",
          },
        });
      } else {
        reset_item_related_form_state();
      }
    }
  }, [testTarget, isEditMode, reset_item_related_form_state]);

  // 関連テストケースを取得する関数（testcaseMappingsから逆引き）
  useEffect(() => {
    if (open && testTarget?.id) {
      processRelatedTestCases();
    }
  }, [open, testTarget?.id, testcaseMappings]);

  const processRelatedTestCases = () => {
    console.log("🚀 TestTargetDetailView - processRelatedTestCases開始");
    console.log("📋 Props確認:", {
      hasTestTarget: !!testTarget,
      testTargetId: testTarget?.id,
      testTargetName: testTarget?.name,
      hasTestcaseMappings: !!testcaseMappings,
      testTargetMappingsSize: testcaseMappings?.testTargetToTestcases.size || 0,
    });

    if (!testTarget?.id) {
      console.log("❌ TestTargetDetailView - testTarget.idがありません");
      setTestCasesError("テストターゲットIDが見つかりません");
      setIsLoadingTestCases(false);
      return;
    }

    setIsLoadingTestCases(true);
    setTestCasesError(null);

    try {
      console.log("🔍 TestTargetDetailView - 関連テストケースを処理中...", {
        testTargetId: testTarget.id,
        hasTestcaseMappings: !!testcaseMappings,
        mappingsSize: testcaseMappings?.testTargetToTestcases.size || 0,
      });

      // 項目単位の紐付け（teststep 由来）が 0 件でもマッピング自体は取得済みのことがある。
      // エラー扱いは「マッピング未取得」のときだけにし、0 件は下の「見つかりませんでした」表示に流す。
      if (!testcaseMappings) {
        console.log(
          "⚠️ TestTargetDetailView - testcaseMappingsが未定義です"
        );
        setTestCasesError("テストケースデータが利用できません");
        setRelatedTestCases([]);
        setIsLoadingTestCases(false);
        return;
      }

      // testcaseMappingsから該当するテストターゲットのテストケースを取得
      const relatedMappings =
        testcaseMappings.testTargetToTestcases.get(testTarget.id) || [];

      console.log("🎯 TestTargetDetailView - 検索結果:", {
        searchKey: testTarget.id,
        found: relatedMappings.length > 0,
        count: relatedMappings.length,
      });

      if (relatedMappings.length > 0) {
        console.log("✅ TestTargetDetailView - 関連テストケースを発見:", {
          testTargetId: testTarget.id,
          count: relatedMappings.length,
        });

        // マッピングをテストケース情報に変換
        const testCaseInfo = relatedMappings.map((mapping) => ({
          testCaseId: mapping.testcaseId,
          testCaseName: mapping.testcaseName,
          testSuiteId: mapping.testsuiteId,
          testSuiteName: mapping.testsuiteName,
          metaId: mapping.metaId,
          stepIndex: mapping.stepIndex,
          stepDescription: mapping.stepDescription,
          stepAction: mapping.stepAction,
        }));

        console.log("📝 テストケース情報を設定:", testCaseInfo);
        setRelatedTestCases(testCaseInfo);
        setIsLoadingTestCases(false);
      } else {
        console.log(
          "❌ TestTargetDetailView - 関連テストケースが見つかりません"
        );
        console.log("🔍 検索キー:", testTarget.id);
        console.log(
          "📋 利用可能なtest_target_idの一覧:",
          Array.from(testcaseMappings.testTargetToTestcases.keys())
        );
        setRelatedTestCases([]);
        setIsLoadingTestCases(false);
      }
    } catch (error) {
      console.error(
        "❌ TestTargetDetailView - 関連テストケース処理エラー:",
        error
      );
      setTestCasesError(
        error instanceof Error ? error.message : "処理に失敗しました"
      );
      setIsLoadingTestCases(false);
    }
  };

  const getTypeLabel = (type: string) => {
    const typeObj = testTargetTypes.find((t) => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const renderSelector = (selector: any) => {
    if (typeof selector === "object" && selector !== null) {
      return (
        <SelectorList>
          {Object.entries(selector).map(([key, value]) => (
            <SelectorItem key={key}>
              <SelectorKey>{key}:</SelectorKey>
              <SelectorValue>{String(value)}</SelectorValue>
            </SelectorItem>
          ))}
        </SelectorList>
      );
    } else {
      return <CodeValue>{String(selector)}</CodeValue>;
    }
  };

  const renderSelectorEditor = () => {
    return (
      <KeyValueEditor>
        {selectorPairs.map((pair, index) => (
          <KeyValueItem key={index}>
            <KeyValueInput
              type="text"
              value={pair.key}
              onChange={(e) =>
                handleSelectorPairChange(index, "key", e.target.value)
              }
              onFocus={() => {
                if (index === 0 && pair.key === "") {
                  handleSelectorPairChange(0, "key", "_default");
                }
              }}
              placeholder="セレクタの名称 (デフォルトの場合は'_default')"
            />
            <span>:</span>
            <KeyValueInput
              type="text"
              value={pair.value}
              onChange={(e) =>
                handleSelectorPairChange(index, "value", e.target.value)
              }
              placeholder="セレクタ (例: #button-id, //button[@id='submit'])"
            />
            {selectorPairs.length > 1 && (
              <RemoveButton
                type="button"
                onClick={() => removeSelectorPair(index)}
              >
                削除
              </RemoveButton>
            )}
          </KeyValueItem>
        ))}
        <AddButton type="button" onClick={addSelectorPair}>
          + セレクター追加
        </AddButton>
      </KeyValueEditor>
    );
  };

  const renderOptionsEditor = () => {
    return (
      <KeyValueEditor>
        {itemDetails.options.map((option, index) => (
          <KeyValueItem key={index}>
            <KeyValueInput
              type="text"
              value={option}
              onChange={(e) => handleOptionChange(index, e.target.value)}
              placeholder="選択肢の値"
            />
            {itemDetails.options.length > 1 && (
              <RemoveButton type="button" onClick={() => removeOption(index)}>
                削除
              </RemoveButton>
            )}
          </KeyValueItem>
        ))}
        <AddButton type="button" onClick={addOption}>
          + 選択肢追加
        </AddButton>
      </KeyValueEditor>
    );
  };

  const handleChange = (field: keyof TestTargetFormData, value: any) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  // 種別変更時: Serviceの場合は親要素をクリア、画面の場合は親はService直下のみに統一
  const handleTypeChange = (value: string) => {
    if (value === "service") {
      setEditedData((prev) => ({ ...prev, type: value, parentId: undefined }));
      setSelectedParentServiceId(null);
      setSelectedParentViewId(null);
      setSelectedParentItemId(null);
    } else if (value === "view" || value === "component" || value === "category") {
      setEditedData((prev) => ({ ...prev, type: value as TestTargetType }));
      setSelectedParentViewId(null);
      setSelectedParentItemId(null);
      // 画面・コンポーネント・カテゴリーは Service 直下のみ
      updateParentId(selectedParentServiceId, null, null);
    } else {
      handleChange("type", value);
    }
  };

  // 親要素選択のハンドラー
  const handleParentServiceChange = (serviceId: string) => {
    const newServiceId = serviceId || null;
    setSelectedParentServiceId(newServiceId);
    // サービスが変更されたら、ビューとアイテムをリセット
    setSelectedParentViewId(null);
    setSelectedParentItemId(null);
    // parentIdを更新
    updateParentId(newServiceId, null, null);
  };

  const handleParentViewChange = (viewId: string) => {
    const newViewId = viewId || null;
    setSelectedParentViewId(newViewId);
    // ビューが変更されたら、アイテムをリセット
    setSelectedParentItemId(null);
    // parentIdを更新
    updateParentId(selectedParentServiceId, newViewId, null);
  };

  const handleParentItemChange = (itemId: string) => {
    const newItemId = itemId || null;
    setSelectedParentItemId(newItemId);
    // parentIdを更新
    updateParentId(selectedParentServiceId, selectedParentViewId, newItemId);
  };

  // parentIdを階層から決定する関数
  const updateParentId = (
    serviceId: string | null,
    viewId: string | null,
    itemId: string | null
  ) => {
    // アイテムが選択されている場合はアイテムID
    if (itemId) {
      handleChange("parentId", itemId);
      return;
    }
    // ビューが選択されている場合はビューID
    if (viewId) {
      handleChange("parentId", viewId);
      return;
    }
    // サービスが選択されている場合はサービスID
    if (serviceId) {
      handleChange("parentId", serviceId);
      return;
    }
    // すべて「なし」の場合はundefined
    handleChange("parentId", undefined);
  };

  const handleItemDetailChange = (field: string, value: any) => {
    setItemDetails((prev) => ({ ...prev, [field]: value }));
  };

  const updatePrimaryItemCategoryFromEntries = (
    entries: ItemCategoryEntry[],
  ): Pick<ReturnType<typeof build_item_details_initial_state>, "itemCategory" | "itemSubCategory" | "itemCategoryEntries"> => {
    const normalized =
      entries.length > 0
        ? entries
        : [{ itemCategory: "Action" as ItemItemCategory, itemSubCategory: "button" as ItemSubCategory }];
    const primary = normalized[0];
    return {
      itemCategory: primary.itemCategory,
      itemSubCategory: primary.itemSubCategory,
      itemCategoryEntries: normalized,
    };
  };

  const handleItemCategoryEntryCategoryChange = (
    index: number,
    value: ItemItemCategory,
  ) => {
    setItemDetails((prev) => {
      const nextSubCategory = defaultSubCategoryForCategory(value);
      const entries = [...(prev.itemCategoryEntries ?? [])];
      entries[index] = {
        ...(entries[index] ?? {}),
        itemCategory: value,
        ...(nextSubCategory ? { itemSubCategory: nextSubCategory } : {}),
      };
      return { ...prev, ...updatePrimaryItemCategoryFromEntries(entries) };
    });
  };

  const handleItemCategoryEntrySubCategoryChange = (
    index: number,
    value: ItemSubCategory | undefined,
  ) => {
    setItemDetails((prev) => {
      const entries = [...(prev.itemCategoryEntries ?? [])];
      const current = entries[index] ?? { itemCategory: "Action" as ItemItemCategory };
      entries[index] = {
        ...current,
        ...(value ? { itemSubCategory: value } : {}),
      };
      if (!value) {
        delete entries[index].itemSubCategory;
      }
      return { ...prev, ...updatePrimaryItemCategoryFromEntries(entries) };
    });
  };

  const addItemCategoryEntry = () => {
    setItemDetails((prev) => ({
      ...prev,
      itemCategoryEntries: [
        ...(prev.itemCategoryEntries ?? []),
        { itemCategory: "Action", itemSubCategory: "button" },
      ],
    }));
  };

  const removeItemCategoryEntry = (index: number) => {
    setItemDetails((prev) => {
      const entries = (prev.itemCategoryEntries ?? []).filter((_, i) => i !== index);
      return { ...prev, ...updatePrimaryItemCategoryFromEntries(entries) };
    });
  };

  const setCheckboxDefaultAt = (index: number, value: boolean) => {
    setItemDetails((prev) => {
      const next = [...(prev.checkboxDefaultValues || [])];
      while (next.length <= index) {
        next.push(false);
      }
      next[index] = value;
      return { ...prev, checkboxDefaultValues: next };
    });
  };

  const handleSelectorPairChange = (
    index: number,
    field: "key" | "value",
    value: string
  ) => {
    setSelectorPairs((prev) => {
      const newPairs = [...prev];
      newPairs[index] = { ...newPairs[index], [field]: value };
      return newPairs;
    });
  };

  const addSelectorPair = () => {
    setSelectorPairs((prev) => [...prev, { key: "", value: "" }]);
  };

  const removeSelectorPair = (index: number) => {
    setSelectorPairs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOptionChange = (index: number, value: string) => {
    setItemDetails((prev) => ({
      ...prev,
      options: prev.options.map((option, i) => (i === index ? value : option)),
    }));
  };

  const addOption = () => {
    setItemDetails((prev) => ({
      ...prev,
      options: [...prev.options, ""],
    }));
  };

  // 新しい選択肢が空の場合は最低1つは表示させる
  React.useEffect(() => {
    if (
      itemDetails.options.length === 0 &&
      itemFormShowsChoiceFields(
        itemDetails.itemCategory,
        itemDetails.itemSubCategory
      )
    ) {
      setItemDetails((prev) => ({
        ...prev,
        options: [""],
      }));
    }
  }, [itemDetails.itemCategory, itemDetails.itemSubCategory]);

  const removeOption = (index: number) => {
    setItemDetails((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleDisplayTextEntryTextChange = (index: number, value: string) => {
    setItemDetails((prev) => ({
      ...prev,
      displayTextEntries: prev.displayTextEntries.map((row, i) =>
        i === index ? { ...row, text: value } : row
      ),
    }));
  };

  const handleDisplayTextEntryConditionChange = (
    entryIndex: number,
    condIndex: number,
    value: string
  ) => {
    setItemDetails((prev) => ({
      ...prev,
      displayTextEntries: prev.displayTextEntries.map((row, i) => {
        if (i !== entryIndex) {
          return row;
        }
        return {
          ...row,
          conditions: row.conditions.map((c, j) =>
            j === condIndex ? value : c
          ),
        };
      }),
    }));
  };

  const addDisplayTextEntry = () => {
    setItemDetails((prev) => ({
      ...prev,
      displayTextEntries: [
        ...prev.displayTextEntries,
        { text: "", conditions: [] },
      ],
    }));
  };

  const removeDisplayTextEntry = (index: number) => {
    setItemDetails((prev) => ({
      ...prev,
      displayTextEntries: prev.displayTextEntries.filter((_, i) => i !== index),
    }));
  };

  const addConditionToDisplayTextEntry = (entryIndex: number) => {
    setItemDetails((prev) => ({
      ...prev,
      displayTextEntries: prev.displayTextEntries.map((row, i) =>
        i === entryIndex
          ? { ...row, conditions: [...row.conditions, ""] }
          : row
      ),
    }));
  };

  const removeConditionFromDisplayTextEntry = (
    entryIndex: number,
    condIndex: number
  ) => {
    setItemDetails((prev) => ({
      ...prev,
      displayTextEntries: prev.displayTextEntries.map((row, i) => {
        if (i !== entryIndex) {
          return row;
        }
        return {
          ...row,
          conditions: row.conditions.filter((_, j) => j !== condIndex),
        };
      }),
    }));
  };

  const renderDisplayTextEntriesEditor = () => {
    return (
      <div>
        {itemDetails.displayTextEntries.map((entry, entryIndex) => (
          <DisplayTextSetCard key={entryIndex}>
            <DisplayTextSetCardTitle>
              表示内容
              {itemDetails.displayTextEntries.length > 1
                ? `（${entryIndex + 1}）`
                : ""}
            </DisplayTextSetCardTitle>
            <Field $fullWidth style={{ marginBottom: 12 }}>
              <Label>表示内容</Label>
              <Input
                value={entry.text}
                onChange={(e) =>
                  handleDisplayTextEntryTextChange(
                    entryIndex,
                    e.target.value
                  )
                }
                placeholder="UI上で表示される内容（未入力可）"
              />
            </Field>
            <Label style={{ display: "block", marginBottom: 6 }}>
              表示条件
            </Label>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#6b7280",
                marginBottom: 8,
              }}
            >
              1 行が 1 条件です。表示条件は複数登録できます。
            </div>
            <KeyValueEditor>
              {entry.conditions.map((condRow, condIndex) => (
                <KeyValueItem key={condIndex}>
                  <KeyValueInput
                    type="text"
                    value={condRow}
                    onChange={(e) =>
                      handleDisplayTextEntryConditionChange(
                        entryIndex,
                        condIndex,
                        e.target.value
                      )
                    }
                    placeholder="例: ログインに成功した場合"
                  />
                  <RemoveButton
                    type="button"
                    onClick={() =>
                      removeConditionFromDisplayTextEntry(
                        entryIndex,
                        condIndex
                      )
                    }
                  >
                    削除
                  </RemoveButton>
                </KeyValueItem>
              ))}
              <AddButton
                type="button"
                onClick={() => addConditionToDisplayTextEntry(entryIndex)}
              >
                + 表示条件を追加
              </AddButton>
            </KeyValueEditor>
            <div style={{ marginTop: 10 }}>
              <RemoveButton
                type="button"
                onClick={() => removeDisplayTextEntry(entryIndex)}
              >
                このセットを削除
              </RemoveButton>
            </div>
          </DisplayTextSetCard>
        ))}
        <AddButton type="button" onClick={addDisplayTextEntry}>
          + セットを追加
        </AddButton>
      </div>
    );
  };

  useEffect(() => {
    if (
      !itemFormShowsCheckboxDefaults(
        itemDetails.itemCategory,
        itemDetails.itemSubCategory
      )
    ) {
      return;
    }
    setItemDetails((prev) => {
      if (
        !itemFormShowsCheckboxDefaults(prev.itemCategory, prev.itemSubCategory)
      ) {
        return prev;
      }
      const n = prev.options.length;
      const cur = prev.checkboxDefaultValues;
      const base = Array.isArray(cur) ? [...cur] : [];
      if (base.length === n) {
        return prev;
      }
      while (base.length < n) {
        base.push(false);
      }
      if (base.length > n) {
        base.length = n;
      }
      return { ...prev, checkboxDefaultValues: base };
    });
  }, [
    itemDetails.itemCategory,
    itemDetails.itemSubCategory,
    itemDetails.options.length,
  ]);

  const handleTransitionServiceChange = (index: number, serviceId: string) => {
    setTransitionServiceSelections((prev) => {
      const updated = [...prev];
      updated[index] = serviceId;
      return updated;
    });

    setItemDetails((prev) => {
      const updatedTransitions = [...prev.transitions];
      if (updatedTransitions[index]) {
        updatedTransitions[index] = {
          ...updatedTransitions[index],
          targetId: "",
        };
      }
      return {
        ...prev,
        transitions: updatedTransitions,
      };
    });
  };

  const moveInternalProcessFlowEntry = (index: number, delta: number) => {
    setItemDetails((prev) => {
      const flow = [...prev.internalProcessFlow];
      const newIndex = index + delta;
      if (newIndex < 0 || newIndex >= flow.length) {
        return prev;
      }
      const tmp = flow[index];
      flow[index] = flow[newIndex];
      flow[newIndex] = tmp;
      return { ...prev, internalProcessFlow: flow };
    });
  };

  const removeInternalProcessFlowEntry = (index: number) => {
    setItemDetails((prev) => ({
      ...prev,
      internalProcessFlow: prev.internalProcessFlow.filter((_, i) => i !== index),
    }));
  };

  const appendInternalProcessFlowEntry = (entry: InternalProcessFlowEntry) => {
    setItemDetails((prev) => ({
      ...prev,
      internalProcessFlow: [...prev.internalProcessFlow, entry],
    }));
  };

  const patchInternalProcessStep = (
    index: number,
    patch: Partial<
      Omit<Extract<InternalProcessFlowEntry, { kind: "step" }>, "kind" | "id">
    >
  ) => {
    setItemDetails((prev) => {
      const flow = [...prev.internalProcessFlow];
      const cur = flow[index];
      if (!cur || cur.kind !== "step") {
        return prev;
      }
      flow[index] = { ...cur, ...patch };
      return { ...prev, internalProcessFlow: flow };
    });
  };

  const handleExecutionControlChange = (field: string, value: boolean) => {
    setExecutionControl((prev) => ({ ...prev, [field]: value }));
  };

  const handleExecutionOptionChange = (field: string, value: string) => {
    setOptions((prev) => ({
      ...prev,
      checkbox: {
        ...prev.checkbox,
        [field]: value,
      },
    }));
  };

  // 種別ごとの親要素必須（画面・コンポーネント・セクション・オーバーレイ・項目・コンポーネント参照は親Service必須；セクション・オーバーレイ・項目・コンポーネント参照は親Viewまたはコンポーネントも必須）
  const TYPES_REQUIRING_PARENT_SERVICE: TestTargetType[] = [
    "category",
    "view",
    "component",
    "section",
    "overlay",
    "item",
    "component-ref",
  ];
  const TYPES_REQUIRING_PARENT_VIEW: TestTargetType[] = [
    "section",
    "overlay",
    "item",
    "component-ref",
  ];

  // 必須項目のバリデーションエラーメッセージを取得する関数
  const getValidationErrors = (): string[] => {
    const errors: string[] = [];

    // 基本情報の必須チェック
    if (!editedData.name?.trim()) {
      errors.push("名前が入力されていません");
    } else if (editedData.name.trim().length > TEST_TARGET_NAME_MAX_LENGTH) {
      errors.push(
        `名前は${TEST_TARGET_NAME_MAX_LENGTH}文字以内にしてください。（現在: ${editedData.name.trim().length}文字）`
      );
    }
    if (!editedData.type) {
      errors.push("種別が選択されていません");
    }

    // 親要素必須チェック（画面・セクション・オーバーレイ・項目は親Service必須）
    if (
      editedData.type &&
      TYPES_REQUIRING_PARENT_SERVICE.includes(editedData.type) &&
      !selectedParentServiceId
    ) {
      errors.push("親Serviceを選択してください");
    }
    // セクション・オーバーレイ・項目は親の画面（View）またはコンポーネントも必須
    if (
      editedData.type &&
      TYPES_REQUIRING_PARENT_VIEW.includes(editedData.type) &&
      !selectedParentViewId
    ) {
      errors.push("親の画面（View）またはコンポーネントを選択してください");
    }

    // Service: Base URL の各行は環境名とドメインの両方が入力されているか、または両方空
    if (editedData.type === "service") {
      const block = getFirstServiceBaseUrlBlockOrEmpty(editedData.params);
      const rows = serviceBlockToRows(block);
      rows.forEach((row, i) => {
        const [envName, domain] = row;
        const envFilled = envName.trim().length > 0;
        const domainFilled = domain.trim().length > 0;
        const rowPrefix = rows.length > 1 ? `${i + 1}行目: ` : "";
        if (envFilled && !domainFilled) {
          errors.push(`${rowPrefix}「ドメイン」を入力してください。`);
        }
        if (!envFilled && domainFilled) {
          errors.push(`${rowPrefix}「環境名」を入力してください。`);
        }
      });
    }

    // Item: セレクタのキーと値は両方入力または両方空
    if (editedData.type === "item") {
      selectorPairs.forEach((pair, i) => {
        const keyFilled = pair.key.trim().length > 0;
        const valueFilled = pair.value.trim().length > 0;
        const rowPrefix =
          selectorPairs.length > 1 ? `${i + 1}行目: ` : "";
        if (keyFilled && !valueFilled) {
          errors.push(`${rowPrefix}「セレクタ」を入力してください。`);
        }
        if (!keyFilled && valueFilled) {
          errors.push(`${rowPrefix}「キー」を入力してください。`);
        }
      });
    }

    // component-ref: 参照先コンポーネントが必須
    if (editedData.type === "component-ref") {
      const refId = (editedData.params as { referenceTargetId?: string } | undefined)?.referenceTargetId;
      if (!refId) {
        errors.push("参照先コンポーネントを選択してください");
      }
    }

    // TODO: 画面遷移がある場合: 各遷移の遷移先サービス（transitionServiceSelections）と遷移先画面（targetId）が必須

    return errors;
  };

  const handleConfirmServiceDelete = async () => {
    if (!onDelete) return;
    if (
      !isServiceDeleteNameConfirmed(
        serviceDeleteConfirmInput,
        testTarget?.name
      )
    ) {
      showError("サービス名が一致しません。確認のためもう一度入力してください。");
      return;
    }
    setIsDeletingService(true);
    try {
      await onDelete();
      setServiceDeleteDialogOpen(false);
    } catch {
      // 失敗時は呼び出し側でトースト等を表示
    } finally {
      setIsDeletingService(false);
    }
  };

  const handleCopyServiceNameForDeleteConfirm = async () => {
    const text = serviceDeleteConfirmationExpectedValue(testTarget?.name);
    try {
      await navigator.clipboard.writeText(text);
      showSuccess("サービス名をコピーしました");
    } catch {
      showError("コピーに失敗しました");
    }
  };

  const handleSave = async () => {
    // 保存前にバリデーションを実行
    const errors = getValidationErrors();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // バリデーションエラーがない場合はエラーメッセージをクリア
    setValidationErrors([]);
    setIsSaving(true);
    try {
      const submitData = { ...editedData, name: editedData.name.trim() };

      // Service種別: 親なし。base_url（ドメイン）・status を params に含めて保存
      if (editedData.type === "service") {
        submitData.parentId = undefined;
        const statusValue = editedData.status || "draft";
        const block = getFirstServiceBaseUrlBlockOrEmpty(editedData.params);
        const rows = serviceBlockToRows(block);
        const baseUrlPersisted = persistServiceParamsBaseUrl(rows);
        submitData.params = {
          base_url: baseUrlPersisted,
          status: statusValue,
        } as ServiceDetails;
      } else if (editedData.type === "item") {
        const transitions =
          itemDetails.transitions.length > 0
            ? itemDetails.transitions
                .filter((t) => t.targetId)
                .map((t) => ({
                  targetId: t.targetId,
                  condition: t.condition || undefined,
                  description: t.description || undefined,
                  status: t.status || undefined,
                  transitionType: t.transitionType ?? "same_tab",
                }))
            : undefined;

        const htmlAttributes = {
          required: itemDetails.required,
          maxLength: itemDetails.maxLength,
          minLength: itemDetails.minLength,
          pattern: itemDetails.pattern || undefined,
          placeholder: itemDetails.placeholder || undefined,
          ...(itemFormShowsCheckboxDefaults(
            itemDetails.itemCategory,
            itemDetails.itemSubCategory
          )
            ? itemDetails.options.length > 0
              ? {
                  checkboxDefaultValues:
                    itemDetails.checkboxDefaultValues.slice(
                      0,
                      itemDetails.options.length
                    ),
                }
              : {}
            : { defaultValue: itemDetails.defaultValue || undefined }),
        };

        // Key:Valueペアからセレクターオブジェクトを構築
        const parsedSelector: Record<string, string> = {};
        selectorPairs.forEach((pair) => {
          if (pair.key && pair.value) {
            parsedSelector[pair.key] = pair.value;
          }
        });

        // 実行制御パラメータとオプションを追加
        const executionControlData = {
          isManual: executionControl.isManual,
          forceAnalyze: executionControl.forceAnalyze,
          failCount: executionControl.failCount,
        };

        const optionsData = {
          checkbox: {
            onStateSelector: options.checkbox.onStateSelector || undefined,
          },
        };

        // NOTE: statusがnullやundefinedの場合は"draft"をデフォルト値として設定
        const statusValue = editedData.status || "draft";
        const internalProcessFlow =
          itemDetails.internalProcessFlow.length > 0
            ? itemDetails.internalProcessFlow
            : undefined;

        const display_text_entries = build_item_display_text_entries_for_save(
          itemDetails.displayTextEntries
        );
        const legacyText =
          first_legacy_text_from_saved_display_entries(display_text_entries);
        const legacyConditions =
          legacy_flat_conditions_from_saved_display_entries(display_text_entries);

        const itemMaskPersisted = normalizeMaskSettings(maskType);
        const itemCategoryEntriesForSave =
          itemDetails.itemCategoryEntries.length > 0
            ? itemDetails.itemCategoryEntries
            : [
                {
                  itemCategory: itemDetails.itemCategory,
                  ...(itemDetails.itemSubCategory
                    ? { itemSubCategory: itemDetails.itemSubCategory }
                    : {}),
                },
              ];
        const primaryItemCategory = itemCategoryEntriesForSave[0];
        submitData.params = {
          ...(legacyText !== undefined ? { text: legacyText } : {}),
          ...(legacyConditions !== undefined && legacyConditions.length > 0
            ? { conditions: legacyConditions }
            : {}),
          ...(display_text_entries !== undefined
            ? { display_text_entries }
            : {}),
          selector: parsedSelector,
          itemCategory: primaryItemCategory.itemCategory,
          ...(primaryItemCategory.itemSubCategory
            ? { itemSubCategory: primaryItemCategory.itemSubCategory }
            : {}),
          ...(itemCategoryEntriesForSave.length > 1
            ? { itemCategories: itemCategoryEntriesForSave }
            : { itemCategories: null }),
          customTypeFlg: itemDetails.customTypeFlg,
          available_choices:
            itemDetails.options.length > 0 ? itemDetails.options : undefined,
          htmlAttributes,
          transitions,
          internalProcessFlow,
          isDownload: itemDetails.isDownload,
          isTemplate: itemDetails.isTemplate ?? null,
          formCategory: itemDetails.formCategory ?? null,
          io: itemDetails.io === "" ? null : itemDetails.io,
          dbMapping: (() => {
            // table と column の両方が入力された行のみ保存対象（部分入力は除外）
            const filtered = itemDetails.dbMapping.filter(
              (m) => m.table.trim() !== "" && m.column.trim() !== ""
            );
            return filtered.length > 0 ? filtered : null;
          })(),
          ...executionControlData,
          options: optionsData,
          status: statusValue,
          ...itemMaskPersisted,
        } as ItemDetails;
      } else {
        const statusValue = editedData.status || "draft";
        // 既存 params に maskType/maskTags が残っていたらクリアし、最新 state から再構築
        const { maskType: _prevMaskType, maskTags: _prevMaskTags, ...restParams } =
          (editedData.params || {}) as {
            maskType?: MaskKind;
            maskTags?: string[];
            [key: string]: unknown;
          };
        const maskPersisted =
          editedData.type === "section" || editedData.type === "overlay"
            ? normalizeMaskSettings(maskType)
            : {};
        let viewParams = {
          ...restParams,
          status: statusValue,
          ...maskPersisted,
        };
        // View のみ paths の空エントリを除外して保存
        if (editedData.type === "view") {
          const currentPaths = getViewPaths(editedData.params);
          const validPaths = currentPaths.filter(
            (p) => p.path && p.path.trim() !== ""
          );
          viewParams = setViewPaths(viewParams, validPaths);
          const rawCids = get_component_ids_from_view_params(viewParams);
          const validCids = rawCids.map((s) => s.trim()).filter((s) => s.length > 0);
          viewParams = { ...viewParams };
          if (validCids.length > 0) {
            (viewParams as ViewDetails).component_ids = validCids;
          } else {
            delete (viewParams as ViewDetails).component_ids;
          }

          (viewParams as {
            componentConditions?: ComponentConditionEntry[] | null;
          }).componentConditions = normalizeComponentConditionsForSave(
            getViewComponentConditions(viewParams),
            validCids
          );
        }
        // Component は JSON スキーマ上プロパティなしのため、status のみ明示的に整える
        if (editedData.type === "component") {
          viewParams = { status: statusValue };
        }
        if (
          editedData.type === "view" ||
          editedData.type === "section" ||
          editedData.type === "overlay"
        ) {
          const validConditions = getParamsConditions(viewParams).filter(
            (c) => c.trim() !== ""
          );
          (viewParams as { conditions?: string[] | null }).conditions =
            validConditions.length > 0 ? validConditions : null;
        }
        submitData.params = viewParams;
      }

      await onSave(submitData);

      // 成功時のみトースト通知を表示
      if (initialMode === "create") {
        showSuccess(
          editedData.type === "service"
            ? "サービスを作成しました"
            : "テストターゲットを作成しました"
        );
        // 作成後もoriginalTestTargetDataをリセット
        // これにより、次に編集モードに入ったときに正しく初期状態が保存される
        setOriginalTestTargetData(null);
      } else {
        showSuccess("テストターゲットを保存しました");

        // 保存後にoriginalTestTargetDataをリセット
        // これにより、次に編集モードに入ったときに正しく初期状態が保存される
        setOriginalTestTargetData(null);
      }

      // 保存成功後にダイアログを閉じる
      setIsEditMode(false);
      onClose();
    } catch (error) {
      // エラー時はエラートーストを表示（成功トーストは表示しない）
      console.error("保存中にエラーが発生しました:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "テストターゲットの保存に失敗しました";
      showError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = (): boolean => {
    if (!originalTestTargetData) {
      console.log("🔍 hasChanges: originalTestTargetData is null, returning false");
      return false;
    }
    
    // options配列の正規化（空文字列のみを含む配列を空配列として扱う）
    const normalizeOptions = (opts: string[]): string[] => {
      const filtered = opts.filter(opt => opt.trim() !== "");
      return filtered.length === 0 ? [] : filtered;
    };
    
    // itemDetailsのoptionsを正規化して比較
    const normalizedCurrentItemDetails = {
      ...itemDetails,
      options: normalizeOptions(itemDetails.options),
    };
    const normalizedOriginalItemDetails = {
      ...originalTestTargetData.itemDetails,
      options: normalizeOptions(originalTestTargetData.itemDetails.options || []),
    };
    
    // 各状態を比較して変更があるかチェック
    const editedDataChanged = 
      JSON.stringify(editedData) !== JSON.stringify(originalTestTargetData.editedData);
    const itemDetailsChanged = 
      JSON.stringify(normalizedCurrentItemDetails) !== JSON.stringify(normalizedOriginalItemDetails);
    const selectorPairsChanged = 
      JSON.stringify(selectorPairs) !== JSON.stringify(originalTestTargetData.selectorPairs);
    const executionControlChanged = 
      JSON.stringify(executionControl) !== JSON.stringify(originalTestTargetData.executionControl);
    const optionsChanged = 
      JSON.stringify(options) !== JSON.stringify(originalTestTargetData.options);
    const transitionServiceSelectionsChanged = 
      JSON.stringify(transitionServiceSelections) !== 
      JSON.stringify(originalTestTargetData.transitionServiceSelections);
    const parentSelectionChanged = 
      selectedParentServiceId !== originalTestTargetData.selectedParentServiceId ||
      selectedParentViewId !== originalTestTargetData.selectedParentViewId ||
      selectedParentItemId !== originalTestTargetData.selectedParentItemId;
    
    // 変更検出の詳細ログを出力
    console.log("🔍 hasChanges: 変更チェック結果", {
      editedDataChanged,
      itemDetailsChanged,
      selectorPairsChanged,
      executionControlChanged,
      optionsChanged,
      transitionServiceSelectionsChanged,
      parentSelectionChanged,
      testTargetId: testTarget?.id,
      testTargetName: testTarget?.name,
    });
    
    if (editedDataChanged) {
      console.log("📝 editedData の差分:", {
        current: editedData,
        original: originalTestTargetData.editedData,
      });
    }
    if (itemDetailsChanged) {
      console.log("📝 itemDetails の差分:", {
        current: itemDetails,
        original: originalTestTargetData.itemDetails,
      });
    }
    if (selectorPairsChanged) {
      console.log("📝 selectorPairs の差分:", {
        current: selectorPairs,
        original: originalTestTargetData.selectorPairs,
      });
    }
    if (executionControlChanged) {
      console.log("📝 executionControl の差分:", {
        current: executionControl,
        original: originalTestTargetData.executionControl,
      });
    }
    if (optionsChanged) {
      console.log("📝 options の差分:", {
        current: options,
        original: originalTestTargetData.options,
      });
    }
    if (transitionServiceSelectionsChanged) {
      console.log("📝 transitionServiceSelections の差分:", {
        current: transitionServiceSelections,
        original: originalTestTargetData.transitionServiceSelections,
      });
    }
    if (parentSelectionChanged) {
      console.log("📝 親要素選択 の差分:", {
        current: { selectedParentServiceId, selectedParentViewId, selectedParentItemId },
        original: {
          selectedParentServiceId: originalTestTargetData.selectedParentServiceId,
          selectedParentViewId: originalTestTargetData.selectedParentViewId,
          selectedParentItemId: originalTestTargetData.selectedParentItemId,
        },
      });
    }
    
    const maskTypeChanged = originalTestTargetData.maskType !== maskType;

    const hasAnyChanges = (
      editedDataChanged ||
      itemDetailsChanged ||
      selectorPairsChanged ||
      executionControlChanged ||
      optionsChanged ||
      transitionServiceSelectionsChanged ||
      parentSelectionChanged ||
      maskTypeChanged
    );

    console.log("🔍 hasChanges: 最終結果", hasAnyChanges);

    return hasAnyChanges;
  };

  // 名前の文字数オーバーを入力中も常時チェックする派生値（保存前のリアルタイム検証用）
  const trimmedNameLength = editedData.name?.trim().length ?? 0;
  const nameLengthError =
    trimmedNameLength > TEST_TARGET_NAME_MAX_LENGTH
      ? `名前は${TEST_TARGET_NAME_MAX_LENGTH}文字以内にしてください。（現在: ${trimmedNameLength}文字）`
      : null;
  // 保存時のエラーと、入力中のリアルタイムエラーをマージして表示する
  const footerValidationErrors =
    nameLengthError && !validationErrors.includes(nameLengthError)
      ? [...validationErrors, nameLengthError]
      : validationErrors;

  const handleCancel = () => {
    if (initialMode === "create") {
      // 新規作成時はダイアログを閉じる
      onClose();
    } else {
      // 編集前の状態に戻す
      if (originalTestTargetData) {
        setEditedData(
          JSON.parse(JSON.stringify(originalTestTargetData.editedData))
        );
        setItemDetails(
          JSON.parse(JSON.stringify(originalTestTargetData.itemDetails))
        );
        setSelectorPairs(
          JSON.parse(JSON.stringify(originalTestTargetData.selectorPairs))
        );
        setExecutionControl(
          JSON.parse(JSON.stringify(originalTestTargetData.executionControl))
        );
        setOptions(JSON.parse(JSON.stringify(originalTestTargetData.options)));
        setTransitionServiceSelections(
          JSON.parse(
            JSON.stringify(
              originalTestTargetData.transitionServiceSelections
            )
          )
        );
        // 親要素の選択状態も元に戻す
        setSelectedParentServiceId(originalTestTargetData.selectedParentServiceId);
        setSelectedParentViewId(originalTestTargetData.selectedParentViewId);
        setSelectedParentItemId(originalTestTargetData.selectedParentItemId);
        // マスク設定を元に戻す
        setMaskType(originalTestTargetData.maskType);
      }
      // 既存アイテムの編集時は閲覧モードに戻る
      setIsEditMode(false);
    }
  };

  const getDialogTitle = () => {
    if (initialMode === "create") {
      return "新規テストターゲット作成";
    }
    if (isEditMode) {
      return `編集: ${String(testTarget?.name || editedData.name)}`;
    }
    return String(testTarget?.name || "テストターゲット詳細");
  };

  const renderBasicTab = () => (
    <TabContent>
      {isEditMode ? (
        <Grid $columns={2}>
          <Field>
            <Label>
              名前<RequiredMark>*</RequiredMark>
            </Label>
            <Input
              value={editedData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="テストターゲットの名前"
              required
            />
          </Field>
          <Field>
            <Label>
              種別<RequiredMark>*</RequiredMark>
            </Label>
            <Select
              value={editedData.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              required
            >
              {testTargetTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field $fullWidth>
            <Label>説明</Label>
            <TextArea
              value={editedData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="テストターゲットの詳細説明"
            />
          </Field>
          <Field>
            <Label>表示順</Label>
            <Input
              type="number"
              min={DEFAULT_DISPLAY_ORDER}
              value={editedData.displayOrder}
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                handleChange(
                  "displayOrder",
                  Number.isFinite(parsed)
                    ? Math.max(DEFAULT_DISPLAY_ORDER, parsed)
                    : DEFAULT_DISPLAY_ORDER
                );
              }}
            />
          </Field>
          <Field $fullWidth>
            <Label>親要素</Label>
            <ParentSelectionContainer>
              {editedData.type === "service" ? (
                <ParentUnavailableMessage>
                  Serviceは親要素を設定できません
                </ParentUnavailableMessage>
              ) : editedData.type === "view" ||
                editedData.type === "component" ||
                editedData.type === "category" ? (
                <>
                  <ParentUnavailableMessage>
                    {editedData.type === "component"
                      ? "コンポーネントは親Serviceのみ設定できます（Service直下にのみ配置されます）"
                      : "親Serviceを選択してください。必要に応じてカテゴリーを指定できます（未指定なら Service 直下に配置されます）"}
                  </ParentUnavailableMessage>
                  <ParentSelectionField>
                    <Label>Service</Label>
                    <Select
                      value={selectedParentServiceId || ""}
                      onChange={(e) => handleParentServiceChange(e.target.value)}
                    >
                      <option value="">選択してください</option>
                      {testTargets
                        .filter((target) => target.type === "service")
                        .filter((target) => {
                          if (initialMode !== "create" && target.id === testTarget?.id) {
                            return false;
                          }
                          return true;
                        })
                        .map((target) => (
                          <option key={target.id} value={target.id}>
                            {String(target.name)}
                          </option>
                        ))}
                    </Select>
                  </ParentSelectionField>
                  {(editedData.type === "view" ||
                    editedData.type === "category") && (
                    <ParentSelectionField>
                      <Label>カテゴリー（任意）</Label>
                      <Select
                        value={(() => {
                          const p = testTargets.find(
                            (t) => t.id === editedData.parentId
                          );
                          return p?.type === "category" ? p.id : "";
                        })()}
                        onChange={(e) => {
                          const catId = e.target.value;
                          handleChange(
                            "parentId",
                            catId || selectedParentServiceId || undefined
                          );
                        }}
                        disabled={!selectedParentServiceId}
                      >
                        <option value="">なし（Service直下）</option>
                        {selectedParentServiceId
                          ? testTargets
                              .filter((target) => target.type === "category")
                              .filter(
                                (target) =>
                                  findRootService(target.id) ===
                                  selectedParentServiceId
                              )
                              .filter(
                                (target) =>
                                  !isSelfOrDescendant(target.id, testTarget?.id)
                              )
                              .map((target) => (
                                <option key={target.id} value={target.id}>
                                  {categoryPathLabel(target.id)}
                                </option>
                              ))
                          : []}
                      </Select>
                    </ParentSelectionField>
                  )}
                </>
              ) : (
                <>
                  <ParentSelectionField>
                    <Label>Service</Label>
                    <Select
                      value={selectedParentServiceId || ""}
                      onChange={(e) => handleParentServiceChange(e.target.value)}
                    >
                      <option value="">選択してください</option>
                      {testTargets
                        .filter((target) => target.type === "service")
                        .filter((target) => {
                          if (initialMode !== "create" && target.id === testTarget?.id) {
                            return false;
                          }
                          return true;
                        })
                        .map((target) => (
                          <option key={target.id} value={target.id}>
                            {String(target.name)}
                          </option>
                        ))}
                    </Select>
                  </ParentSelectionField>
                  <ParentSelectionField>
                    <Label>View / コンポーネント</Label>
                    <Select
                      value={selectedParentViewId || ""}
                      onChange={(e) => handleParentViewChange(e.target.value)}
                      disabled={!selectedParentServiceId}
                    >
                      <option value="">選択してください</option>
                      {selectedParentServiceId
                        ? testTargets
                            .filter((target) => {
                              if (target.type !== "view" && target.type !== "component")
                                return false;
                              if (initialMode !== "create" && target.id === testTarget?.id) {
                                return false;
                              }
                              const targetService = findRootService(target.id);
                              return targetService === selectedParentServiceId;
                            })
                            .map((target) => (
                              <option key={target.id} value={target.id}>
                                {String(target.name)}
                              </option>
                            ))
                        : []}
                    </Select>
                  </ParentSelectionField>
                  <ParentSelectionField>
                    <Label>Item</Label>
                    <Select
                      value={selectedParentItemId || ""}
                      onChange={(e) => handleParentItemChange(e.target.value)}
                      disabled={!selectedParentViewId}
                    >
                      <option value="">なし</option>
                      {selectedParentViewId
                        ? testTargets
                            .filter((target) => {
                              if (
                                target.type === "service" ||
                                target.type === "view" ||
                                target.type === "component"
                              ) {
                                return false;
                              }
                              if (initialMode !== "create" && target.id === testTarget?.id) {
                                return false;
                              }
                              const containerId = findParentViewOrComponent(target.id);
                              return containerId === selectedParentViewId;
                            })
                            .map((target) => (
                              <option key={target.id} value={target.id}>
                                {String(target.name)}
                              </option>
                            ))
                        : []}
                    </Select>
                  </ParentSelectionField>
                </>
              )}
            </ParentSelectionContainer>
          </Field>
          {editedData.type === "service" && (
            <Field $fullWidth>
              <Label>Base URL（ドメイン）</Label>
              {(() => {
                const block = getFirstServiceBaseUrlBlockOrEmpty(
                  editedData.params
                );
                const rows = serviceBlockToRows(block);
                return (
                  <BaseUrlBlock>
                    {rows.map((row, rowIndex) => (
                      <BaseUrlKvRow key={rowIndex}>
                        <Input
                          value={row[0]}
                          onChange={(e) => {
                            setEditedData((prev) => {
                              const prevBlock =
                                getFirstServiceBaseUrlBlockOrEmpty(prev.params);
                              const prevRows = serviceBlockToRows(prevBlock);
                              const nextRows = [...prevRows];
                              nextRows[rowIndex] = [
                                e.target.value,
                                nextRows[rowIndex][1],
                              ];
                              return {
                                ...prev,
                                params: setServiceBaseUrls(prev.params || {}, [
                                  rowsToServiceBlock(nextRows),
                                ]),
                              };
                            });
                          }}
                          placeholder="環境名（例: dev）"
                        />
                        <Input
                          value={row[1]}
                          onChange={(e) => {
                            setEditedData((prev) => {
                              const prevBlock =
                                getFirstServiceBaseUrlBlockOrEmpty(prev.params);
                              const prevRows = serviceBlockToRows(prevBlock);
                              const nextRows = [...prevRows];
                              nextRows[rowIndex] = [
                                nextRows[rowIndex][0],
                                e.target.value,
                              ];
                              return {
                                ...prev,
                                params: setServiceBaseUrls(prev.params || {}, [
                                  rowsToServiceBlock(nextRows),
                                ]),
                              };
                            });
                          }}
                          placeholder="ドメイン（例: https://example.com）"
                        />
                        <RemoveButton
                          type="button"
                          onClick={() => {
                            setEditedData((prev) => {
                              const prevBlock =
                                getFirstServiceBaseUrlBlockOrEmpty(prev.params);
                              let prevRows = serviceBlockToRows(prevBlock);
                              prevRows = prevRows.filter((_, i) => i !== rowIndex);
                              if (prevRows.length === 0) {
                                prevRows = [["", ""]];
                              }
                              return {
                                ...prev,
                                params: setServiceBaseUrls(prev.params || {}, [
                                  rowsToServiceBlock(prevRows),
                                ]),
                              };
                            });
                          }}
                          title="この行を削除"
                        >
                          削除
                        </RemoveButton>
                      </BaseUrlKvRow>
                    ))}
                    <BaseUrlBlockActions>
                      <AddButton
                        type="button"
                        onClick={() => {
                          setEditedData((prev) => {
                            const prevBlock =
                              getFirstServiceBaseUrlBlockOrEmpty(prev.params);
                            const prevRows = serviceBlockToRows(prevBlock);
                            const nextRows: [string, string][] = [
                              ...prevRows,
                              ["", ""] as [string, string],
                            ];
                            return {
                              ...prev,
                              params: setServiceBaseUrls(prev.params || {}, [
                                rowsToServiceBlock(nextRows),
                              ]),
                            };
                          });
                        }}
                      >
                        環境を追加
                      </AddButton>
                    </BaseUrlBlockActions>
                  </BaseUrlBlock>
                );
              })()}
            </Field>
          )}
          {editedData.type === "view" && (
            <Field $fullWidth>
              <Label>Path（パス）</Label>
              {(() => {
                const currentPaths = getViewPaths(editedData.params);
                const paths =
                  currentPaths.length > 0 ? currentPaths : [{ path: "" }];
                return (
                  <>
                    {paths.map((pathObj, index) => (
                      <PathInputRow key={index}>
                        <Input
                          value={pathObj.path || ""}
                          onChange={(e) => {
                            const newPaths = paths.map((p, i) =>
                              i === index
                                ? { ...p, path: e.target.value }
                                : p
                            );
                            setEditedData((prev) => ({
                              ...prev,
                              params: setViewPaths(prev.params || {}, newPaths),
                            }));
                          }}
                          placeholder="URLパスを入力（例: /login, /dashboard）"
                        />
                        <RemoveButton
                          type="button"
                          onClick={() => {
                            const newPaths = paths.filter((_, i) => i !== index);
                            const normalized =
                              newPaths.length > 0
                                ? newPaths
                                : [{ path: "" }];
                            setEditedData((prev) => ({
                              ...prev,
                              params: setViewPaths(
                                prev.params || {},
                                normalized
                              ),
                            }));
                          }}
                          title="パスを削除"
                        >
                          削除
                        </RemoveButton>
                      </PathInputRow>
                    ))}
                    <AddButton
                      type="button"
                      onClick={() => {
                        const currentPaths = getViewPaths(editedData.params);
                        const newPaths = [
                          ...currentPaths,
                          { path: "" },
                        ];
                        setEditedData((prev) => ({
                          ...prev,
                          params: setViewPaths(prev.params || {}, newPaths),
                        }));
                      }}
                    >
                      パスを追加
                    </AddButton>
                  </>
                );
              })()}
            </Field>
          )}
          {(editedData.type === "section" ||
            editedData.type === "overlay") && (
            <Field $fullWidth>
              <Label>表示条件</Label>
              {(() => {
                const conditions = getParamsConditions(editedData.params);
                const updateConditions = (next: string[]) => {
                  // state は常に string[] で保持し、空 → null のクリア信号変換は handleSave で行う
                  setEditedData((prev) => ({
                    ...prev,
                    params: {
                      ...(prev.params || {}),
                      conditions: next,
                    } as SectionDetails,
                  }));
                };
                return (
                  <>
                    {conditions.map((cond, index) => (
                      <PathInputRow key={index}>
                        <Input
                          value={cond}
                          onChange={(e) => {
                            const next = conditions.map((c, i) =>
                              i === index ? e.target.value : c
                            );
                            updateConditions(next);
                          }}
                          placeholder="例: ログイン状態の場合 / 管理者ロールの場合 / 初回ログイン時のみ / 広告表示対象の場合"
                        />
                        <RemoveButton
                          type="button"
                          onClick={() =>
                            updateConditions(
                              conditions.filter((_, i) => i !== index)
                            )
                          }
                          title="条件を削除"
                        >
                          削除
                        </RemoveButton>
                      </PathInputRow>
                    ))}
                    <AddButton
                      type="button"
                      onClick={() => updateConditions([...conditions, ""])}
                    >
                      条件を追加
                    </AddButton>
                  </>
                );
              })()}
            </Field>
          )}
          {editedData.type === "view" && (
            <Field $fullWidth>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                  marginBottom: 4,
                }}
              >
                <Label style={{ marginBottom: 0 }}>画面の表示条件</Label>
                <FieldHelpButton
                  help={FIELD_HELP.viewDisplayConditions}
                  ariaLabel="画面の表示条件の説明"
                />
              </div>
              {(() => {
                const conditions = getParamsConditions(editedData.params);
                const updateConditions = (next: string[]) => {
                  setEditedData((prev) => ({
                    ...prev,
                    params: {
                      ...(prev.params || {}),
                      conditions: next,
                    } as ViewDetails,
                  }));
                };
                return (
                  <>
                    {conditions.map((cond, index) => (
                      <PathInputRow key={index}>
                        <Input
                          value={cond}
                          onChange={(e) => {
                            const next = conditions.map((c, i) =>
                              i === index ? e.target.value : c
                            );
                            updateConditions(next);
                          }}
                          placeholder="例: ログイン状態の場合 / 管理者ロールの場合 / 初回ログイン時のみ / 広告表示対象の場合"
                        />
                        <RemoveButton
                          type="button"
                          onClick={() =>
                            updateConditions(
                              conditions.filter((_, i) => i !== index)
                            )
                          }
                          title="条件を削除"
                        >
                          削除
                        </RemoveButton>
                      </PathInputRow>
                    ))}
                    <AddButton
                      type="button"
                      onClick={() => updateConditions([...conditions, ""])}
                    >
                      条件を追加
                    </AddButton>
                  </>
                );
              })()}
            </Field>
          )}
          {editedData.type === "view" && (
            <Field $fullWidth>
              <Label>共通コンポーネント</Label>
              {(() => {
                const ids = normalize_component_ids_for_edit(
                  get_component_ids_from_view_params(editedData.params)
                );
                const has_any_stored_id = ids.some(
                  (id) => id.trim().length > 0
                );
                const current_component_conditions =
                  getViewComponentConditions(editedData.params);
                const write_conditions_for_id = (
                  componentId: string,
                  next_conditions: string[]
                ): void => {
                  setEditedData((prev) => ({
                    ...prev,
                    params: {
                      ...(prev.params || {}),
                      componentConditions: upsertComponentCondition(
                        current_component_conditions,
                        componentId,
                        next_conditions
                      ),
                    },
                  }));
                };
                const set_params_component_ids = (next: string[]) => {
                  setEditedData((prev) => ({
                    ...prev,
                    params: {
                      ...(prev.params || {}),
                      component_ids: next,
                    },
                  }));
                };
                const change_component_id = (
                  old_id: string,
                  new_id: string,
                  index: number
                ): void => {
                  const next_ids = [...ids];
                  next_ids[index] = new_id;
                  setEditedData((prev) => ({
                    ...prev,
                    params: {
                      ...(prev.params || {}),
                      component_ids: next_ids,
                      componentConditions: swapComponentIdPreservingConditions(
                        current_component_conditions,
                        old_id,
                        new_id
                      ),
                    },
                  }));
                };
                const remove_component_id_row = (index: number): void => {
                  const removed = (ids[index] ?? "").trim();
                  const next = ids.filter((_, i) => i !== index);
                  const normalized = next.length > 0 ? next : [""];
                  setEditedData((prev) => ({
                    ...prev,
                    params: {
                      ...(prev.params || {}),
                      component_ids: normalized,
                      componentConditions: removeComponentCondition(
                        current_component_conditions,
                        removed
                      ),
                    },
                  }));
                };

                const render_conditions_block = (
                  trimmed_cid: string,
                  index: number
                ) => {
                  const current = getConditionsForComponentId(
                    current_component_conditions,
                    trimmed_cid
                  );
                  return (
                    <ConditionsBlock>
                      <ConditionsBlockLabel>表示条件</ConditionsBlockLabel>
                      {current.map((cond, ci) => (
                        <PathInputRow
                          key={`cond-${index}-${ci}`}
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            value={cond}
                            onChange={(e) => {
                              const next = current.map((c, i) =>
                                i === ci ? e.target.value : c
                              );
                              write_conditions_for_id(trimmed_cid, next);
                            }}
                            placeholder="例: ログイン状態の場合 / 管理者ロールの場合 / 初回ログイン時のみ / 広告表示対象の場合"
                          />
                          <RemoveButton
                            type="button"
                            onClick={() =>
                              write_conditions_for_id(
                                trimmed_cid,
                                current.filter((_, i) => i !== ci)
                              )
                            }
                            title="条件を削除"
                          >
                            削除
                          </RemoveButton>
                        </PathInputRow>
                      ))}
                      <AddButton
                        type="button"
                        onClick={() =>
                          write_conditions_for_id(trimmed_cid, [
                            ...current,
                            "",
                          ])
                        }
                      >
                        条件を追加
                      </AddButton>
                    </ConditionsBlock>
                  );
                };

                if (
                  available_view_components.length === 0 &&
                  !has_any_stored_id
                ) {
                  return (
                    <div
                      style={{
                        color: "#64748b",
                        fontSize: "0.875rem",
                        margin: 0,
                      }}
                    >
                      共通コンポーネントが存在しません
                    </div>
                  );
                }
                if (
                  available_view_components.length === 0 &&
                  has_any_stored_id
                ) {
                  return (
                    <PathInputContainer>
                      {ids.map((cid, index) => {
                        const trimmed_cid = cid.trim();
                        return (
                          <div key={`orphan-${index}`}>
                            <PathInputRow style={{ marginBottom: 0 }}>
                              <div
                                style={{
                                  flex: 1,
                                  margin: 0,
                                  fontSize: "0.875rem",
                                  color: "#1f2937",
                                }}
                              >
                                {trimmed_cid || "（空）"}
                              </div>
                              <IconButton
                                type="button"
                                onClick={() => remove_component_id_row(index)}
                                title="この行を削除"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  flexShrink: 0,
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </PathInputRow>
                            <ComponentErrorText>
                              存在しない共通コンポーネントです
                            </ComponentErrorText>
                            {trimmed_cid
                              ? render_conditions_block(trimmed_cid, index)
                              : null}
                          </div>
                        );
                      })}
                      <ComponentOutlineAddButton
                        type="button"
                        onClick={() => {
                          set_params_component_ids([...ids, ""]);
                        }}
                      >
                        <AddIcon sx={{ fontSize: 18 }} />
                        共通コンポーネントを追加
                      </ComponentOutlineAddButton>
                    </PathInputContainer>
                  );
                }
                return (
                  <PathInputContainer>
                    {ids.map((cid, index) => {
                      const resolved = resolve_component_by_stored_id(
                        cid,
                        testTargets
                      );
                      const orphan = Boolean(cid.trim()) && !resolved;
                      const elsewhere_selected_ids = new Set<string>();
                      ids.forEach((s, i) => {
                        if (i === index) {
                          return;
                        }
                        const t = s.trim();
                        if (t.length > 0) {
                          elsewhere_selected_ids.add(t);
                        }
                      });
                      const trimmed_cid = cid.trim();
                      const duplicate =
                        Boolean(trimmed_cid) &&
                        Boolean(resolved) &&
                        elsewhere_selected_ids.has(trimmed_cid);
                      const selectable_components =
                        available_view_components.filter(
                          (c) => !elsewhere_selected_ids.has(c.id)
                        );
                      return (
                        <div key={`row-${index}`}>
                          <PathInputRow style={{ marginBottom: 0 }}>
                            <PathSelect
                              value={cid}
                              onChange={(e) =>
                                change_component_id(
                                  cid,
                                  e.target.value,
                                  index
                                )
                              }
                            >
                              <option value="">（選択してください）</option>
                              {orphan ? (
                                <option value={cid}>
                                  {cid}（参照できません）
                                </option>
                              ) : null}
                              {duplicate && resolved ? (
                                <option value={cid}>
                                  {resolved.name}（他行と重複）
                                </option>
                              ) : null}
                              {selectable_components.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </PathSelect>
                            <IconButton
                              type="button"
                              onClick={() => remove_component_id_row(index)}
                              title="この行を削除"
                              style={{
                                width: "32px",
                                height: "32px",
                                flexShrink: 0,
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </PathInputRow>
                          {orphan ? (
                            <ComponentErrorText>
                              存在しない共通コンポーネントです
                            </ComponentErrorText>
                          ) : null}
                          {duplicate ? (
                            <ComponentErrorText>
                              同じ共通コンポーネントが他の行でも選択されています
                            </ComponentErrorText>
                          ) : null}
                          {trimmed_cid && !duplicate
                            ? render_conditions_block(trimmed_cid, index)
                            : null}
                        </div>
                      );
                    })}
                    <ComponentOutlineAddButton
                      type="button"
                      onClick={() => {
                        set_params_component_ids([...ids, ""]);
                      }}
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                      共通コンポーネントを追加
                    </ComponentOutlineAddButton>
                  </PathInputContainer>
                );
              })()}
            </Field>
          )}
          {editedData.type === "component-ref" && (
            <Field $fullWidth>
              <Label>参照先コンポーネント<span style={{ color: "red" }}> *</span></Label>
              <Select
                value={(editedData.params as { referenceTargetId?: string } | undefined)?.referenceTargetId ?? ""}
                onChange={(e) =>
                  setEditedData((prev) => ({
                    ...prev,
                    params: { ...(prev.params || {}), referenceTargetId: e.target.value },
                  }))
                }
              >
                <option value="">選択してください</option>
                {(selectedParentServiceId
                  ? get_components_under_service(selectedParentServiceId, testTargets)
                  : []
                ).map((c) => (
                  <option key={c.id} value={c.test_target_id ?? c.id}>{c.name}</option>
                ))}
              </Select>
            </Field>
          )}
          <Field>
            <Label>ステータス</Label>
            <Select
              value={editedData.status || "draft"}
              onChange={(e) =>
                handleChange("status", e.target.value as ReviewStatus)
              }
            >
              {(Object.keys(STATUS_LABELS) as ReviewStatus[]).map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          </Field>
        </Grid>
      ) : (
        <div>
          <InfoRow>
            <InfoLabel>ID</InfoLabel>
            <InfoValue>{String(testTarget?.id || "-")}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>名前</InfoLabel>
            <InfoValue>{String(editedData.name || "-")}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>種別</InfoLabel>
            <InfoValue>
              <TypeBadge type={editedData.type || "service"}>
                {getTypeLabel(editedData.type || "service")}
              </TypeBadge>
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>説明</InfoLabel>
            <InfoValue>{String(editedData.description || "-")}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>表示順</InfoLabel>
            <InfoValue>{String(editedData.displayOrder || 0)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>親要素ID</InfoLabel>
            <InfoValue>
              {editedData.type === "service"
                ? "Serviceは親要素を持てません"
                : String(editedData.parentId || "選択してください")}
            </InfoValue>
          </InfoRow>
          {editedData.type === "service" && (
            <InfoRow>
              <InfoLabel>Base URL（ドメイン）</InfoLabel>
              <InfoValue>
                {(() => {
                  const block = getFirstServiceBaseUrlBlockOrEmpty(
                    editedData.params
                  );
                  const clean = sanitizeBaseUrlBlockForPersistence(block);
                  const entries = Object.entries(clean);
                  if (entries.length === 0) {
                    return (
                      <PathListBox>Base URL が設定されていません</PathListBox>
                    );
                  }
                  return (
                    <PathListBox>
                      {entries.map(([envKey, url]) => (
                        <PathListItem key={envKey}>
                          <strong>{envKey}</strong>
                          {": "}
                          {url}
                        </PathListItem>
                      ))}
                    </PathListBox>
                  );
                })()}
              </InfoValue>
            </InfoRow>
          )}
          {editedData.type === "view" && (
            <InfoRow>
              <InfoLabel>Path（パス）</InfoLabel>
              <InfoValue>
                {(() => {
                  const paths = getViewPaths(editedData.params);
                  const validPaths = paths.filter(
                    (p) => p.path && p.path.trim() !== ""
                  );
                  return validPaths.length > 0 ? (
                    <PathListBox>
                      {validPaths.map((pathObj, index) => (
                        <PathListItem key={index}>
                          {pathObj.path}
                        </PathListItem>
                      ))}
                    </PathListBox>
                  ) : (
                    <PathListBox>パスが設定されていません</PathListBox>
                  );
                })()}
              </InfoValue>
            </InfoRow>
          )}
          {(editedData.type === "view" ||
            editedData.type === "section" ||
            editedData.type === "overlay") && (
            <InfoRow>
              <InfoLabel>
                {editedData.type === "view" ? "画面の表示条件" : "表示条件"}
              </InfoLabel>
              <InfoValue>
                {(() => {
                  const conditions = getParamsConditions(editedData.params).filter(
                    (c) => c.trim() !== ""
                  );
                  return conditions.length > 0 ? (
                    <PathListBox>
                      {conditions.map((cond, index) => (
                        <PathListItem key={index}>{cond}</PathListItem>
                      ))}
                    </PathListBox>
                  ) : (
                    <PathListBox>表示条件が設定されていません</PathListBox>
                  );
                })()}
              </InfoValue>
            </InfoRow>
          )}
          {editedData.type === "view" && (
            <InfoRow>
              <InfoLabel>共通コンポーネント</InfoLabel>
              <InfoValue>
                {(() => {
                  const ids = unique_trimmed_component_ids(
                    get_component_ids_from_view_params(editedData.params)
                  );
                  const service_id_for_list =
                    findRootService(testTarget?.id || "") ||
                    findRootService(editedData.parentId || "") ||
                    "";
                  const available_for_read = service_id_for_list
                    ? get_components_under_service(
                        service_id_for_list,
                        testTargets
                      )
                    : [];
                  const component_conditions = getViewComponentConditions(
                    editedData.params
                  );
                  const conditions_by_id = new Map<string, string[]>(
                    component_conditions.map((e) => [
                      e.componentId,
                      e.conditions.filter((c) => c.trim() !== ""),
                    ])
                  );
                  if (ids.length === 0) {
                    if (available_for_read.length === 0) {
                      return "共通コンポーネントが存在しません";
                    }
                    return "共通コンポーネントが設定されていません";
                  }
                  return (
                    <PathList>
                      {ids.map((cid, index) => {
                        const resolved = resolve_component_by_stored_id(
                          cid,
                          testTargets
                        );
                        const conditions = conditions_by_id.get(cid) ?? [];
                        return (
                          <div key={`ro-${index}-${cid}`}>
                            <PathItem>
                              {resolved ? resolved.name : cid}
                            </PathItem>
                            {!resolved ? (
                              <ComponentErrorText>
                                存在しない共通コンポーネントです
                              </ComponentErrorText>
                            ) : null}
                            {conditions.length > 0 ? (
                              <ConditionsReadOnlyList>
                                {conditions.map((cond, ci) => (
                                  <ConditionsReadOnlyItem
                                    key={`ro-cond-${index}-${ci}`}
                                  >
                                    {cond}
                                  </ConditionsReadOnlyItem>
                                ))}
                              </ConditionsReadOnlyList>
                            ) : null}
                          </div>
                        );
                      })}
                    </PathList>
                  );
                })()}
              </InfoValue>
            </InfoRow>
          )}
          <InfoRow>
            <InfoLabel>ステータス</InfoLabel>
            <InfoValue>
              {(() => {
                const status = editedData.status || "approved";
                return (
                  <StatusBadge status={status}>
                    {STATUS_LABELS[status] || STATUS_LABELS.approved}
                  </StatusBadge>
                );
              })()}
            </InfoValue>
          </InfoRow>
        </div>
      )}
    </TabContent>
  );

  const renderItemDetailsTab = () => {
    if (
      (!isEditMode && testTarget?.type !== "item") ||
      (isEditMode && editedData.type !== "item")
    ) {
      return (
        <TabContent>
          <NotAvailableMessage>
            項目タイプでないため、詳細設定は利用できません。
          </NotAvailableMessage>
        </TabContent>
      );
    }

    return (
      <TabContent>
        {isEditMode ? (
          <div>
            <StandaloneField $fullWidth>
              <Label>表示内容・表示条件</Label>
              {renderDisplayTextEntriesEditor()}
            </StandaloneField>

            <Grid $columns={2}>
              <Field $fullWidth>
                <Label>項目カテゴリ / サブカテゴリ</Label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {itemDetails.itemCategoryEntries.map((entry, index) => (
                    <div
                      key={index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <Select
                        value={entry.itemCategory}
                        onChange={(e) =>
                          handleItemCategoryEntryCategoryChange(
                            index,
                            e.target.value as ItemItemCategory,
                          )
                        }
                      >
                        {ITEM_CATEGORY_OPTIONS.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </Select>
                      <Select
                        value={entry.itemSubCategory ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          handleItemCategoryEntrySubCategoryChange(
                            index,
                            v === "" ? undefined : (v as ItemSubCategory),
                          );
                        }}
                      >
                        {entry.itemCategory === "Display" && (
                          <option value="">（未指定）</option>
                        )}
                        {SUBCATEGORIES_BY_ITEM_CATEGORY[entry.itemCategory].map(
                          (o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          )
                        )}
                      </Select>
                      <MuiIconButton
                        size="small"
                        onClick={() => removeItemCategoryEntry(index)}
                        disabled={itemDetails.itemCategoryEntries.length <= 1}
                        aria-label="カテゴリ行を削除"
                      >
                        <DeleteIcon fontSize="small" />
                      </MuiIconButton>
                    </div>
                  ))}
                  <MuiConfirmButton
                    type="button"
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addItemCategoryEntry}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    行を追加
                  </MuiConfirmButton>
                </div>
              </Field>
              <Field>
                {/* ユーザーに説明が必要 */}
                <Label>オプション</Label>
                <CheckboxContainer>
                  <Checkbox
                    checked={itemDetails.customTypeFlg}
                    onChange={(e) =>
                      handleItemDetailChange("customTypeFlg", e.target.checked)
                    }
                  />
                  <CheckboxLabel>カスタム要素</CheckboxLabel>
                </CheckboxContainer>
              </Field>
              <Field $fullWidth>
                <Label>
                  セレクター
                </Label>
                {renderSelectorEditor()}
              </Field>

              {itemFormShowsChoiceFields(
                itemDetails.itemCategory,
                itemDetails.itemSubCategory
              ) && (
                <Field $fullWidth>
                  <Label>選択肢</Label>
                  {renderOptionsEditor()}
                </Field>
              )}
            </Grid>

            {itemFormShowsDefaultValueField(
              itemDetails.itemCategory,
              itemDetails.itemSubCategory
            ) && (
              <StandaloneField $fullWidth>
                <Label>初期値</Label>
                <Input
                  list={itemDefaultValueDatalistId}
                  value={itemDetails.defaultValue}
                  onChange={(e) =>
                    handleItemDetailChange("defaultValue", e.target.value)
                  }
                  placeholder="▼ 候補から選ぶか、値を直接入力"
                  autoComplete="off"
                />
                <datalist id={itemDefaultValueDatalistId}>
                  {itemDetails.options.map((opt, i) => (
                    <option key={i} value={opt}>
                      {opt === "" ? "（空の値）" : opt}
                    </option>
                  ))}
                </datalist>
              </StandaloneField>
            )}

            {itemFormShowsCheckboxDefaults(
              itemDetails.itemCategory,
              itemDetails.itemSubCategory
            ) && (
              <StandaloneField $fullWidth>
                <Label>初期値（各選択肢）</Label>
                {itemDetails.options.map((opt, index) => {
                  const checked =
                    itemDetails.checkboxDefaultValues[index] === true;
                  const groupName = `item-checkbox-initial-${index}`;
                  return (
                    <CheckboxDefaultRow key={index}>
                      <Label>
                        {opt.trim() !== ""
                          ? opt
                          : `選択肢 ${index + 1}（未入力）`}
                      </Label>
                      <RadioRow>
                        <RadioOptionLabel>
                          <RadioInput
                            name={groupName}
                            checked={checked}
                            onChange={() => setCheckboxDefaultAt(index, true)}
                          />
                          True（チェックされている）
                        </RadioOptionLabel>
                        <RadioOptionLabel>
                          <RadioInput
                            name={groupName}
                            checked={!checked}
                            onChange={() => setCheckboxDefaultAt(index, false)}
                          />
                          False（チェックされていない）
                        </RadioOptionLabel>
                      </RadioRow>
                    </CheckboxDefaultRow>
                  );
                })}
              </StandaloneField>
            )}

            {itemFormShowsTextConstraints(
              itemDetails.itemCategory,
              itemDetails.itemSubCategory
            ) && (
              <>
                <Grid $columns={2}>
                  <Field>
                    <Label>最大文字数</Label>
                    <Input
                      type="number"
                      value={itemDetails.maxLength || ""}
                      onChange={(e) =>
                        handleItemDetailChange(
                          "maxLength",
                          e.target.value ? parseInt(e.target.value) : undefined
                        )
                      }
                    />
                  </Field>
                  <Field>
                    <Label>最小文字数</Label>
                    <Input
                      type="number"
                      value={itemDetails.minLength || ""}
                      onChange={(e) =>
                        handleItemDetailChange(
                          "minLength",
                          e.target.value ? parseInt(e.target.value) : undefined
                        )
                      }
                    />
                  </Field>
                  <Field>
                    <Label>パターン</Label>
                    <Input
                      value={itemDetails.pattern}
                      onChange={(e) =>
                        handleItemDetailChange("pattern", e.target.value)
                      }
                      placeholder="[A-Za-z0-9]+"
                    />
                  </Field>
                  <Field>
                    <Label>プレースホルダー</Label>
                    <Input
                      value={itemDetails.placeholder}
                      onChange={(e) =>
                        handleItemDetailChange("placeholder", e.target.value)
                      }
                    />
                  </Field>
                </Grid>
              </>
            )}

            <StandaloneField $fullWidth>
              <CheckboxContainer>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <Label style={{ marginBottom: 0 }}>必須項目</Label>
                  <FieldHelpButton
                    help={FIELD_HELP.itemRequiredField}
                    ariaLabel="必須項目の説明"
                  />
                </div>
                <Checkbox
                  checked={itemDetails.required}
                  onChange={(e) =>
                    handleItemDetailChange("required", e.target.checked)
                  }
                />
                <CheckboxLabel>必須</CheckboxLabel>
              </CheckboxContainer>
            </StandaloneField>

            <StandaloneField $fullWidth>
              <Label>繰り返し要素</Label>
              <RadioRow>
                {ITEM_IS_TEMPLATE_OPTIONS.map((opt) => (
                  <RadioOptionLabel key={opt.value || "none"}>
                    <RadioInput
                      name="item-is-template"
                      checked={
                        isTemplateToRadioValue(itemDetails.isTemplate) ===
                        opt.value
                      }
                      onChange={() =>
                        handleItemDetailChange(
                          "isTemplate",
                          radioValueToIsTemplate(opt.value)
                        )
                      }
                    />
                    {opt.label}
                  </RadioOptionLabel>
                ))}
              </RadioRow>
            </StandaloneField>

            <StandaloneField $fullWidth>
              <Label>入力フォーム分類</Label>
              <Input
                value={itemDetails.formCategory ?? ""}
                onChange={(e) => {
                  const trimmed = e.target.value.trim();
                  handleItemDetailChange(
                    "formCategory",
                    trimmed === "" ? undefined : trimmed
                  );
                }}
                placeholder="例: ログイン（同じフォーム処理にまとめる Item のラベル）"
              />
              <div
                style={{
                  marginTop: 4,
                  fontSize: "0.75rem",
                  color: "#6b7280",
                }}
              >
                同じフォーム処理（例: ログイン）にまとめたい Item に同じラベルを付ける
              </div>
            </StandaloneField>
          </div>
        ) : (
          testTarget?.type === "item" &&
          as_item_details(testTarget?.params) &&
          (() => {
            const itemParams = as_item_details(testTarget.params)!;
            const itemCategoryEntries = resolveItemCategoryEntries(itemParams);
            return (
              <div>
                {(() => {
                  const blocks = item_params_to_display_view_entries(itemParams);
                  if (blocks.length === 0) {
                    return (
                      <InfoRow>
                        <InfoLabel>表示内容・表示条件</InfoLabel>
                        <InfoValue>—</InfoValue>
                      </InfoRow>
                    );
                  }
                  return blocks.map((block, bi) => (
                    <InfoRow key={bi}>
                      <InfoLabel>
                        表示内容・表示条件
                        {blocks.length > 1 ? `（${bi + 1}）` : ""}
                      </InfoLabel>
                      <InfoValue>
                        <div
                          style={{
                            fontSize: "0.875rem",
                            lineHeight: 1.5,
                          }}
                        >
                          <div style={{ marginBottom: 8 }}>
                            <span style={{ color: "#64748b" }}>表示内容: </span>
                            {block.text && block.text.trim().length > 0
                              ? block.text
                              : "—"}
                          </div>
                          <div>
                            {!block.conditions ||
                            block.conditions.length === 0 ? (
                              <div>
                                <span style={{ color: "#64748b" }}>
                                  表示条件1:{" "}
                                </span>
                                <span
                                  style={{
                                    color: "#94a3b8",
                                    fontSize: "0.8125rem",
                                  }}
                                >
                                  —
                                </span>
                              </div>
                            ) : (
                              block.conditions.map((line, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    marginTop: idx > 0 ? 8 : 0,
                                  }}
                                >
                                  <span style={{ color: "#64748b" }}>
                                    表示条件{idx + 1}:{" "}
                                  </span>
                                  <span>{line}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </InfoValue>
                    </InfoRow>
                  ));
                })()}
                <InfoRow>
                  <InfoLabel>セレクター</InfoLabel>
                  <InfoValue>{renderSelector(itemParams.selector)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>項目カテゴリ / サブカテゴリ</InfoLabel>
                  <InfoValue>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {itemCategoryEntries.map((entry, index) => (
                        <div
                          key={`${entry.itemCategory}-${entry.itemSubCategory ?? "none"}-${index}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip variant="primary">{entry.itemCategory}</Chip>
                          <span style={{ color: "#94a3b8" }}>/</span>
                          <Chip variant="secondary">
                            {entry.itemSubCategory ?? "—"}
                          </Chip>
                        </div>
                      ))}
                    </div>
                    {itemParams.customTypeFlg && (
                      <Chip variant="success">カスタム</Chip>
                    )}
                  </InfoValue>
                </InfoRow>

                {itemParams.available_choices &&
                  itemParams.available_choices.length > 0 && (
                    <InfoRow>
                      <InfoLabel>選択肢</InfoLabel>
                      <InfoValue>
                        <ChipContainer>
                          {itemParams.available_choices.map((option, index) => (
                            <Chip key={index} variant="secondary">
                              {typeof option === "string"
                                ? option
                                : option.value ||
                                  option.label ||
                                  String(option)}
                            </Chip>
                          ))}
                        </ChipContainer>
                      </InfoValue>
                    </InfoRow>
                  )}

                {(() => {
                  const r = resolveItemCategoryAndSubCategory(itemParams);
                  return (
                    r.itemSubCategory === "checkbox" &&
                    Array.isArray(itemParams.available_choices) &&
                    itemParams.available_choices.length > 0 &&
                    Array.isArray(
                      itemParams.htmlAttributes?.checkboxDefaultValues
                    )
                  );
                })() && (
                    <InfoRow>
                      <InfoLabel>初期値（各選択肢）</InfoLabel>
                      <InfoValue>
                        <ChipContainer>
                          {(itemParams.available_choices ?? []).map((option, index) => {
                            const label =
                              typeof option === "string"
                                ? option
                                : option.value ||
                                  option.label ||
                                  "";
                            const raw =
                              itemParams.htmlAttributes?.checkboxDefaultValues?.[
                                index
                              ];
                            const isTrue =
                              raw === true ||
                              (typeof raw === "string" && raw === "true");
                            return (
                              <Chip key={index} variant="secondary">
                                {(label.trim() !== ""
                                  ? label
                                  : `選択肢${index + 1}`) +
                                  ": " +
                                  (isTrue ? "True" : "False")}
                              </Chip>
                            );
                          })}
                        </ChipContainer>
                      </InfoValue>
                    </InfoRow>
                  )}

                {(() => {
                  const ha = itemParams.htmlAttributes;
                  const r = resolveItemCategoryAndSubCategory(itemParams);
                  if (!ha) {
                    return null;
                  }
                  const showHtmlAttrsRow =
                    ha.required ||
                    ha.maxLength != null ||
                    ha.minLength != null ||
                    Boolean(ha.pattern) ||
                    Boolean(ha.placeholder) ||
                    (r.itemSubCategory !== "checkbox" && Boolean(ha.defaultValue));
                  if (!showHtmlAttrsRow) {
                    return null;
                  }
                  return (
                    <InfoRow>
                      <InfoLabel>HTML属性</InfoLabel>
                      <InfoValue>
                        <ChipContainer>
                          {ha.required && (
                            <Chip variant="error">必須</Chip>
                          )}
                          {ha.maxLength != null && (
                            <Chip variant="secondary">
                              最大長: {String(ha.maxLength)}
                            </Chip>
                          )}
                          {ha.minLength != null && (
                            <Chip variant="secondary">
                              最小長: {String(ha.minLength)}
                            </Chip>
                          )}
                          {ha.pattern && (
                            <Chip variant="warning">
                              パターン: {String(ha.pattern)}
                            </Chip>
                          )}
                          {ha.placeholder && (
                            <Chip variant="secondary">
                              プレースホルダー: {String(ha.placeholder)}
                            </Chip>
                          )}
                          {r.itemSubCategory !== "checkbox" && ha.defaultValue && (
                            <Chip variant="secondary">
                              初期値: {String(ha.defaultValue)}
                            </Chip>
                          )}
                        </ChipContainer>
                      </InfoValue>
                    </InfoRow>
                  );
                })()}

                <InfoRow>
                  <InfoLabel>繰り返し要素</InfoLabel>
                  <InfoValue>
                    {itemParams.isTemplate === true ? (
                      <Chip variant="primary">繰り返し要素である</Chip>
                    ) : itemParams.isTemplate === false ? (
                      <Chip variant="secondary">繰り返し要素ではない</Chip>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>未指定</span>
                    )}
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>入力フォーム分類</InfoLabel>
                  <InfoValue>
                    {itemParams.formCategory ? (
                      <Chip variant="secondary">{itemParams.formCategory}</Chip>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </InfoValue>
                </InfoRow>

              </div>
            );
          })()
        )}
      </TabContent>
    );
  };

  const renderExecutionControlTab = () => {
    if (
      (!isEditMode && testTarget?.type !== "item") ||
      (isEditMode && editedData.type !== "item")
    ) {
      return (
        <TabContent>
          <NotAvailableMessage>
            項目タイプでないため、実行制御設定は利用できません。
          </NotAvailableMessage>
        </TabContent>
      );
    }

    return (
      <TabContent>
        {isEditMode ? (
          <div>
            <SectionTitle>実行制御パラメータ</SectionTitle>
            <OptionGroup>
              <OptionGroupTitle>実行モード設定</OptionGroupTitle>
              <CheckboxContainer>
                <Checkbox
                  checked={executionControl.isManual}
                  onChange={(e) =>
                    handleExecutionControlChange("isManual", e.target.checked)
                  }
                />
                <CheckboxLabel>手動実行モード (isManual)</CheckboxLabel>
              </CheckboxContainer>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  marginLeft: "24px",
                  marginTop: "4px",
                }}
              >
                手動実行モードかどうかを指定します。trueの場合、ユーザーがブラウザ上で直接操作を行います。
              </div>
            </OptionGroup>

            <OptionGroup>
              <OptionGroupTitle>要素解析設定</OptionGroupTitle>
              <CheckboxContainer>
                <Checkbox
                  checked={executionControl.forceAnalyze}
                  onChange={(e) =>
                    handleExecutionControlChange(
                      "forceAnalyze",
                      e.target.checked
                    )
                  }
                />
                <CheckboxLabel>強制解析 (forceAnalyze)</CheckboxLabel>
              </CheckboxContainer>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  marginLeft: "24px",
                  marginTop: "4px",
                }}
              >
                既にtargetが設定されていても、要素解析を強制実行するかどうかを指定します。
              </div>
            </OptionGroup>

            <OptionGroup>
              <OptionGroupTitle>ダウンロード設定 (isDownload)</OptionGroupTitle>
              <CheckboxContainer>
                <Checkbox
                  checked={itemDetails.isDownload}
                  onChange={(e) =>
                    handleItemDetailChange("isDownload", e.target.checked)
                  }
                />
                <CheckboxLabel>ダウンロード発生可能性</CheckboxLabel>
              </CheckboxContainer>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  marginTop: "4px",
                }}
              >
                この要素の操作によりダウンロードが発生する可能性がある場合にチェックします。
              </div>
            </OptionGroup>

            <SectionTitle>失敗カウント管理</SectionTitle>
            <OptionGroup>
              <OptionGroupTitle>連続失敗回数 (failCount)</OptionGroupTitle>
              <Field>
                <Input
                  type="number"
                  min="0"
                  value={executionControl.failCount}
                  onChange={(e) =>
                    setExecutionControl((prev) => ({
                      ...prev,
                      failCount: parseInt(e.target.value) || 0,
                    }))
                  }
                  style={{ width: "120px" }}
                />
              </Field>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  marginTop: "8px",
                }}
              >
                テスト実行時に連続して失敗した回数です。閾値（デフォルト:
                3回）に達すると自動的に「手動実行モード」が有効になります。
                <br />
                手動で0にリセットすることで、失敗カウントをクリアできます。
              </div>
              {executionControl.failCount > 0 && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "8px 12px",
                    background:
                      executionControl.failCount >= 3 ? "#fee2e2" : "#fef3c7",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    color:
                      executionControl.failCount >= 3 ? "#dc2626" : "#d97706",
                  }}
                >
                  {executionControl.failCount >= 3
                    ? `連続 ${executionControl.failCount} 回失敗しています。手動実行モードが設定されます。`
                    : `連続 ${executionControl.failCount} 回失敗しています。`}
                </div>
              )}
            </OptionGroup>

            <SectionTitle>実行オプション</SectionTitle>
            <OptionGroup>
              <OptionGroupTitle>チェックボックス設定</OptionGroupTitle>
              <Field>
                <Label>ON状態セレクタ (onStateSelector)</Label>
                <Input
                  value={options.checkbox.onStateSelector}
                  onChange={(e) =>
                    handleExecutionOptionChange(
                      "onStateSelector",
                      e.target.value
                    )
                  }
                  placeholder="#prescribed_holidays1:checked"
                />
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  ON状態を示すセレクタを指定します。チェックボックスの状態判定に使用されます。
                </div>
              </Field>
            </OptionGroup>
          </div>
        ) : (
          testTarget?.type === "item" &&
          as_item_details(testTarget?.params) &&
          (() => {
            const itemParams = as_item_details(testTarget.params)!;
            return (
              <div>
                <SectionTitle>実行制御パラメータ</SectionTitle>

                <InfoRow>
                  <InfoLabel>手動実行モード</InfoLabel>
                  <InfoValue>
                    <Chip variant={itemParams.isManual ? "warning" : "success"}>
                      {itemParams.isManual ? "有効" : "無効"}
                    </Chip>
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>強制解析</InfoLabel>
                  <InfoValue>
                    <Chip
                      variant={itemParams.forceAnalyze ? "warning" : "success"}
                    >
                      {itemParams.forceAnalyze ? "有効" : "無効"}
                    </Chip>
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>ダウンロード発生可能性</InfoLabel>
                  <InfoValue>
                    <Chip
                      variant={itemParams.isDownload ? "warning" : "success"}
                    >
                      {itemParams.isDownload ? "有効" : "無効"}
                    </Chip>
                  </InfoValue>
                </InfoRow>

                <SectionTitle>失敗カウント</SectionTitle>
                <InfoRow>
                  <InfoLabel>連続失敗回数</InfoLabel>
                  <InfoValue>
                    <Chip
                      variant={
                        (itemParams.failCount || 0) >= 3
                          ? "error"
                          : (itemParams.failCount || 0) > 0
                          ? "warning"
                          : "success"
                      }
                    >
                      {itemParams.failCount || 0} 回
                    </Chip>
                    {(itemParams.failCount || 0) >= 3 && (
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "0.75rem",
                          color: "#dc2626",
                        }}
                      >
                        閾値超過（手動実行モードに設定）
                      </span>
                    )}
                  </InfoValue>
                </InfoRow>

                {itemParams.options?.checkbox?.onStateSelector && (
                  <>
                    <SectionTitle>実行オプション</SectionTitle>
                    <InfoRow>
                      <InfoLabel>ON状態セレクタ</InfoLabel>
                      <InfoValue>
                        <CodeValue>
                          {itemParams.options.checkbox.onStateSelector}
                        </CodeValue>
                      </InfoValue>
                    </InfoRow>
                  </>
                )}
              </div>
            );
          })()
        )}
      </TabContent>
    );
  };

  const renderMaskSettingsTab = () => {
    return (
      <TabContent>
        {isEditMode ? (
          <Grid $columns={1}>
            <Field $fullWidth>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                  marginBottom: 6,
                }}
              >
                <Label style={{ marginBottom: 0 }}>マスク種別</Label>
                <FieldHelpButton
                  help={FIELD_HELP.maskType}
                  ariaLabel="マスク種別の説明"
                />
              </div>
              <Select
                value={maskType}
                onChange={(e) =>
                  setMaskType(e.target.value as MaskKind | "")
                }
              >
                <option value="">なし</option>
                <option value="cover">cover（色付きボックスで被せる）</option>
                <option value="hide">hide（visibility: hidden）</option>
                <option value="remove">remove（DOM から除去）</option>
              </Select>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  marginTop: "4px",
                }}
              >
                この testtarget 自身をマスク対象とする場合に設定します。
              </div>
            </Field>
          </Grid>
        ) : (
          <div>
            <InfoRow>
              <InfoLabel>マスク種別</InfoLabel>
              <InfoValue>{maskType || "（なし）"}</InfoValue>
            </InfoRow>
          </div>
        )}
      </TabContent>
    );
  };

  const renderMetaInfoTab = () => {
    if (!testTarget?.metaInfo) {
      return (
        <TabContent>
          <NotAvailableMessage>メタ情報が利用できません。</NotAvailableMessage>
        </TabContent>
      );
    }

    return (
      <TabContent>
        <InfoRow>
          <InfoLabel>作成日時</InfoLabel>
          <InfoValue>
            {testTarget.metaInfo.createdAt
              ? new Date(testTarget.metaInfo.createdAt).toLocaleString()
              : "-"}
          </InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>作成者</InfoLabel>
          <InfoValue>
            <UserNameDisplay
              userId={testTarget.metaInfo.createdBy}
              userName={testTarget.metaInfo.createdBy ? userNames[testTarget.metaInfo.createdBy] : undefined}
            />
          </InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>更新日時</InfoLabel>
          <InfoValue>
            {testTarget.metaInfo.modifiedAt
              ? new Date(testTarget.metaInfo.modifiedAt).toLocaleString()
              : "-"}
          </InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>更新者</InfoLabel>
          <InfoValue>
            <UserNameDisplay
              userId={testTarget.metaInfo.modifiedBy}
              userName={testTarget.metaInfo.modifiedBy ? userNames[testTarget.metaInfo.modifiedBy] : undefined}
            />
          </InfoValue>
        </InfoRow>
      </TabContent>
    );
  };

  const renderRelatedTestCasesTab = () => {
    console.log("🎨 renderRelatedTestCasesTab - 状態確認:", {
      isLoadingTestCases,
      testCasesError,
      relatedTestCasesLength: relatedTestCases.length,
      relatedTestCases,
      activeTab,
    });

    return (
      <TabContent>
        {isLoadingTestCases ? (
          <NotAvailableMessage>関連テストケースを取得中...</NotAvailableMessage>
        ) : testCasesError ? (
          <NotAvailableMessage>エラー: {testCasesError}</NotAvailableMessage>
        ) : relatedTestCases.length === 0 ? (
          <NotAvailableMessage>
            このアイテムを使用しているテストケースは見つかりませんでした。
          </NotAvailableMessage>
        ) : (
          <div>
            <div
              style={{
                marginBottom: "12px",
                fontSize: "0.85rem",
                color: "#6b7280",
              }}
            >
              このアイテムを使用している{" "}
              <strong>{relatedTestCases.length}</strong>{" "}
              件のテストケースが見つかりました
            </div>
            <CompactTable>
              <thead>
                <tr>
                  <CompactTableHeader>テストスイート</CompactTableHeader>
                  <CompactTableHeader>テストケース名</CompactTableHeader>
                  <CompactTableHeader>ID</CompactTableHeader>
                  <CompactTableHeader>ステップ情報</CompactTableHeader>
                </tr>
              </thead>
              <tbody>
                {relatedTestCases.map((testCase, index) => {
                  // テストスイートへのリンクを生成
                  const testsuiteUrl =
                    workspaceId && projectId
                      ? `${
                          apiClient?.config?.webBaseUrl ||
                          "https://dev.sstf.agest.app"
                        }/${workspaceId}/${projectId}/tests/${
                          testCase.testSuiteId
                        }/testcases`
                      : null;

                  return (
                    <CompactTableRow key={`${testCase.testCaseId}-${index}`}>
                      <CompactTableCell>
                        {testsuiteUrl ? (
                          <a
                            href={testsuiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#3b82f6",
                              textDecoration: "none",
                              fontSize: "0.75rem",
                              fontWeight: "500",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration =
                                "underline";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = "none";
                            }}
                          >
                            {testCase.testSuiteName}
                          </a>
                        ) : (
                          <div style={{ fontSize: "0.75rem" }}>
                            {testCase.testSuiteName}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: "0.65rem",
                            color: "#6b7280",
                            marginTop: "2px",
                          }}
                        >
                          ID: {testCase.testSuiteId}
                        </div>
                      </CompactTableCell>
                      <CompactTableCell>
                        <div style={{ fontWeight: "500", marginBottom: "2px" }}>
                          {testCase.testCaseName}
                        </div>
                        <div style={{ fontSize: "0.65rem", color: "#6b7280" }}>
                          ID: {testCase.testCaseId}
                        </div>
                      </CompactTableCell>
                      <CompactTableCell>
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "#6b7280",
                            fontFamily: "monospace",
                          }}
                        >
                          {testCase.metaId}
                        </div>
                      </CompactTableCell>
                      <CompactTableCell>
                        {testCase.stepIndex !== undefined ? (
                          <div>
                            <div style={{ fontSize: "0.7rem" }}>
                              ステップ {testCase.stepIndex + 1}
                            </div>
                            {testCase.stepDescription && (
                              <div
                                style={{
                                  fontSize: "0.65rem",
                                  color: "#6b7280",
                                }}
                              >
                                {testCase.stepDescription}
                              </div>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </CompactTableCell>
                    </CompactTableRow>
                  );
                })}
              </tbody>
            </CompactTable>
          </div>
        )}
      </TabContent>
    );
  };

  const renderBehaviorDetailsTab = () => {
    if (
      (!isEditMode && testTarget?.type !== "item") ||
      (isEditMode && editedData.type !== "item")
    ) {
      return (
        <TabContent>
          <NotAvailableMessage>
            項目タイプでないため、動作詳細は利用できません。
          </NotAvailableMessage>
        </TabContent>
      );
    }

    const flowReorderControls = (index: number) => (
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => moveInternalProcessFlowEntry(index, -1)}
          disabled={index === 0}
          style={{ padding: "4px 8px", fontSize: "0.75rem" }}
        >
          上へ
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => moveInternalProcessFlowEntry(index, 1)}
          disabled={index === itemDetails.internalProcessFlow.length - 1}
          style={{ padding: "4px 8px", fontSize: "0.75rem" }}
        >
          下へ
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={() => removeInternalProcessFlowEntry(index)}
          style={{ padding: "4px 8px", fontSize: "0.75rem" }}
        >
          削除
        </Button>
      </div>
    );

    return (
      <TabContent>
        {isEditMode ? (
          <div>
            <BehaviorSection>
              <SectionTitleRow>
                <BehaviorSubsectionTitle>I/O 分類</BehaviorSubsectionTitle>
              </SectionTitleRow>
              <BehaviorHint>
                この項目が DB に対して入力 (input) / 出力 (output) / 双方 (both) のどれを行うかを指定します。DB アクセス系のテスト条件生成に使用します。
              </BehaviorHint>
              <RadioRow>
                {ITEM_IO_OPTIONS.map((opt) => (
                  <RadioOptionLabel key={opt.value || "none"}>
                    <RadioInput
                      name="item-io"
                      checked={itemDetails.io === opt.value}
                      onChange={() =>
                        setItemDetails({ ...itemDetails, io: opt.value })
                      }
                    />
                    {opt.label}
                  </RadioOptionLabel>
                ))}
              </RadioRow>
            </BehaviorSection>

            <BehaviorSection>
              <SectionTitleRow>
                <BehaviorSubsectionTitle>DB マッピング</BehaviorSubsectionTitle>
              </SectionTitleRow>
              <BehaviorHint>
                この項目に紐づく DB テーブル・カラム。1 項目が複数 DB カラムに対応する場合（例: フルネーム → first_name + last_name）は複数行を登録します。
              </BehaviorHint>
              {itemDetails.dbMapping.length > 0 && (
                <Grid $columns={2}>
                  {itemDetails.dbMapping.map((row, i) => (
                    <React.Fragment key={i}>
                      <Field>
                        <Label>テーブル</Label>
                        <Input
                          value={row.table}
                          onChange={(e) =>
                            setItemDetails({
                              ...itemDetails,
                              dbMapping: itemDetails.dbMapping.map((m, j) =>
                                j === i ? { ...m, table: e.target.value } : m
                              ),
                            })
                          }
                          placeholder="例: users"
                        />
                      </Field>
                      <Field>
                        <Label>カラム</Label>
                        <div style={{ display: "flex", gap: 6 }}>
                          <Input
                            value={row.column}
                            onChange={(e) =>
                              setItemDetails({
                                ...itemDetails,
                                dbMapping: itemDetails.dbMapping.map((m, j) =>
                                  j === i ? { ...m, column: e.target.value } : m
                                ),
                              })
                            }
                            placeholder="例: first_name"
                            style={{ flex: 1 }}
                          />
                          <Button
                            type="button"
                            variant="danger"
                            onClick={() =>
                              setItemDetails({
                                ...itemDetails,
                                dbMapping: itemDetails.dbMapping.filter(
                                  (_, j) => j !== i
                                ),
                              })
                            }
                            style={{ padding: "4px 8px", fontSize: "0.75rem" }}
                          >
                            削除
                          </Button>
                        </div>
                      </Field>
                    </React.Fragment>
                  ))}
                </Grid>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setItemDetails({
                    ...itemDetails,
                    dbMapping: [
                      ...itemDetails.dbMapping,
                      { table: "", column: "" },
                    ],
                  })
                }
                style={{ marginTop: 8 }}
              >
                ＋ 行を追加
              </Button>
            </BehaviorSection>

            <BehaviorSection>
              <SectionTitleRow>
                <BehaviorSubsectionTitle>内部処理</BehaviorSubsectionTitle>
                <FieldHelpButton
                  help={FIELD_HELP.internalProcess}
                  ariaLabel="内部処理の説明"
                />
              </SectionTitleRow>
              <BehaviorHint>
                処理が発生する順番に登録してください。非同期で同時に走る処理は「並列グループ開始／終了」でくくり、終了マーカーまで待機する流れとして表現できます。
              </BehaviorHint>
              <FlowTimeline>
                {itemDetails.internalProcessFlow.map((entry, index) => {
                  if (entry.kind === "parallel_start") {
                    return (
                      <FlowParallelMarker key={entry.id} $variant="start">
                        <span>並列グループ開始</span>
                        {flowReorderControls(index)}
                      </FlowParallelMarker>
                    );
                  }
                  if (entry.kind === "parallel_end") {
                    return (
                      <FlowParallelMarker key={entry.id} $variant="end">
                        <span>並列グループ終了（ここまで待機）</span>
                        {flowReorderControls(index)}
                      </FlowParallelMarker>
                    );
                  }
                  return (
                    <FlowStepCard key={entry.id}>
                      <FlowStepMeta>
                        <span>
                          処理 {index + 1}
                          {entry.processName
                            ? ` · ${entry.processName}`
                            : ""}
                        </span>
                        {flowReorderControls(index)}
                      </FlowStepMeta>
                      <Grid $columns={2}>
                        <Field $fullWidth>
                          <Label>処理名</Label>
                          <Input
                            value={entry.processName}
                            onChange={(e) =>
                              patchInternalProcessStep(index, {
                                processName: e.target.value,
                              })
                            }
                            placeholder="例: 合計金額の再計算、保存API呼び出し"
                          />
                        </Field>
                        <Field $fullWidth>
                          <Label>処理内容</Label>
                          <TextArea
                            value={entry.processContent}
                            onChange={(e) =>
                              patchInternalProcessStep(index, {
                                processContent: e.target.value,
                              })
                            }
                            placeholder={INTERNAL_PROCESS_CONTENT_PLACEHOLDER}
                          />
                        </Field>
                        <Field>
                          <Label>正常終了時</Label>
                          <TextArea
                            value={entry.expectedChangeOnSuccess}
                            onChange={(e) =>
                              patchInternalProcessStep(index, {
                                expectedChangeOnSuccess: e.target.value,
                              })
                            }
                            placeholder="例: 合計金額が500円になる、モーダルが表示される"
                          />
                        </Field>
                        <Field>
                          <Label>エラー発生時</Label>
                          <TextArea
                            value={entry.expectedChangeOnError}
                            onChange={(e) =>
                              patchInternalProcessStep(index, {
                                expectedChangeOnError: e.target.value,
                              })
                            }
                            placeholder="例: バリデーションエラーメッセージが表示される"
                          />
                        </Field>
                        <Field>
                          <Label>非同期</Label>
                          <CheckboxContainer>
                            <Checkbox
                              checked={entry.isAsync}
                              onChange={(e) =>
                                patchInternalProcessStep(index, {
                                  isAsync: e.target.checked,
                                })
                              }
                            />
                            <CheckboxLabel>完了まで待機が必要</CheckboxLabel>
                          </CheckboxContainer>
                        </Field>
                        <Field>
                          <Label>実行条件（前提）</Label>
                          <Input
                            value={entry.precondition}
                            onChange={(e) =>
                              patchInternalProcessStep(index, {
                                precondition: e.target.value,
                              })
                            }
                            placeholder="例: ○○が入力されている場合のみ"
                          />
                        </Field>
                      </Grid>
                    </FlowStepCard>
                  );
                })}
              </FlowTimeline>
              {itemDetails.internalProcessFlow.length === 0 && (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    color: "#6b7280",
                    fontSize: "0.875rem",
                    border: "1px dashed #d1d5db",
                    borderRadius: 8,
                    background: "#f9fafb",
                    marginBottom: 12,
                  }}
                >
                  内部処理が未登録です。下のボタンから追加できます。
                </div>
              )}
              <FlowActionsRow>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    appendInternalProcessFlowEntry(
                      createDefaultInternalProcessStep()
                    )
                  }
                >
                  ＋ 処理を追加
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    appendInternalProcessFlowEntry({
                      id: newInternalFlowEntryId(),
                      kind: "parallel_start",
                    })
                  }
                >
                  ＋ 並列グループ開始
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    appendInternalProcessFlowEntry({
                      id: newInternalFlowEntryId(),
                      kind: "parallel_end",
                    })
                  }
                >
                  ＋ 並列グループ終了
                </Button>
              </FlowActionsRow>
            </BehaviorSection>

            <BehaviorSection>
              <SectionTitleRow>
                <BehaviorSubsectionTitle>遷移</BehaviorSubsectionTitle>
                <FieldHelpButton
                  help={FIELD_HELP.transitionSection}
                  ariaLabel="遷移の説明"
                />
              </SectionTitleRow>

              {itemDetails.transitions.length > 0 ? (
                <TabContainer>
                  <TabList>
                    {itemDetails.transitions.map((_, index) => (
                      <TabButton
                        key={index}
                        type="button"
                        $active={activeTransitionTab === index}
                        onClick={() => setActiveTransitionTab(index)}
                      >
                        遷移 {index + 1}
                        <TabCloseButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const updatedTransitions =
                              itemDetails.transitions.filter(
                                (_, i) => i !== index
                              );
                            setItemDetails({
                              ...itemDetails,
                              transitions: updatedTransitions,
                            });
                            setTransitionServiceSelections((prev) =>
                              prev.filter((_, i) => i !== index)
                            );
                            if (
                              activeTransitionTab >= updatedTransitions.length
                            ) {
                              setActiveTransitionTab(
                                Math.max(0, updatedTransitions.length - 1)
                              );
                            }
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                          </svg>
                        </TabCloseButton>
                      </TabButton>
                    ))}
                    <AddTabButton
                      type="button"
                      onClick={() => {
                        const defaultServiceId = getOwnerServiceId();
                        const newTransition = {
                          targetId: "",
                          condition: "",
                          description: "",
                          status: "approved" as ReviewStatus,
                          transitionType: "same_tab" as ItemTransitionType,
                        };
                        setItemDetails({
                          ...itemDetails,
                          transitions: [
                            ...itemDetails.transitions,
                            newTransition,
                          ],
                        });
                        setTransitionServiceSelections((prev) => [
                          ...prev,
                          defaultServiceId,
                        ]);
                        setActiveTransitionTab(itemDetails.transitions.length);
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                      </svg>
                      追加
                    </AddTabButton>
                  </TabList>

                  {itemDetails.transitions[activeTransitionTab] && (
                    <TabPanel>
                      <Grid $columns={2}>
                        <Field>
                          <Label>
                            遷移先サービス<RequiredMark>*</RequiredMark>
                          </Label>
                          <Select
                            value={activeTransitionServiceId}
                            onChange={(e) =>
                              handleTransitionServiceChange(
                                activeTransitionTab,
                                e.target.value
                              )
                            }
                            required
                          >
                            <option value="">
                              サービスを選択してください
                            </option>
                            {serviceTargets.map((service) => (
                              <option key={service.id} value={service.id}>
                                {String(service.name)}
                              </option>
                            ))}
                          </Select>
                        </Field>
                        <Field>
                          <Label>
                            遷移先画面<RequiredMark>*</RequiredMark>
                          </Label>
                          <Select
                            value={
                              itemDetails.transitions[activeTransitionTab]
                                .targetId
                            }
                            onChange={(e) => {
                              const updatedTransitions = [
                                ...itemDetails.transitions,
                              ];
                              updatedTransitions[activeTransitionTab] = {
                                ...updatedTransitions[activeTransitionTab],
                                targetId: e.target.value,
                              };
                              setItemDetails({
                                ...itemDetails,
                                transitions: updatedTransitions,
                              });
                            }}
                            required
                            disabled={!activeTransitionServiceId}
                          >
                            <option value="">
                              {activeTransitionServiceId
                                ? "遷移先を選択してください"
                                : "サービスを先に選択してください"}
                            </option>
                            {availableViewsForActiveTransition.map((target) => (
                              <option key={target.id} value={target.id}>
                                {String(target.name)} (
                                {getTypeLabel(target.type)})
                              </option>
                            ))}
                          </Select>
                        </Field>
                        <Field $fullWidth>
                          <Label>遷移タイプ</Label>
                          <Select
                            value={
                              itemDetails.transitions[activeTransitionTab]
                                .transitionType ?? "same_tab"
                            }
                            onChange={(e) => {
                              const updatedTransitions = [
                                ...itemDetails.transitions,
                              ];
                              updatedTransitions[activeTransitionTab] = {
                                ...updatedTransitions[activeTransitionTab],
                                transitionType: e.target
                                  .value as ItemTransitionType,
                              };
                              setItemDetails({
                                ...itemDetails,
                                transitions: updatedTransitions,
                              });
                            }}
                          >
                            {(
                              Object.keys(
                                ITEM_TRANSITION_TYPE_LABELS
                              ) as ItemTransitionType[]
                            ).map((tt) => (
                              <option key={tt} value={tt}>
                                {ITEM_TRANSITION_TYPE_LABELS[tt]}
                              </option>
                            ))}
                          </Select>
                        </Field>
                        <Field $fullWidth>
                          <Label>遷移条件 (任意)</Label>
                          <Input
                            value={
                              itemDetails.transitions[activeTransitionTab]
                                .condition
                            }
                            onChange={(e) => {
                              const updatedTransitions = [
                                ...itemDetails.transitions,
                              ];
                              updatedTransitions[activeTransitionTab] = {
                                ...updatedTransitions[activeTransitionTab],
                                condition: e.target.value,
                              };
                              setItemDetails({
                                ...itemDetails,
                                transitions: updatedTransitions,
                              });
                            }}
                            placeholder="例: ログインに成功した場合、エラーが発生した場合など"
                          />
                        </Field>
                        <Field $fullWidth>
                          <Label>遷移説明</Label>
                          <TextArea
                            value={
                              itemDetails.transitions[activeTransitionTab]
                                .description
                            }
                            onChange={(e) => {
                              const updatedTransitions = [
                                ...itemDetails.transitions,
                              ];
                              updatedTransitions[activeTransitionTab] = {
                                ...updatedTransitions[activeTransitionTab],
                                description: e.target.value,
                              };
                              setItemDetails({
                                ...itemDetails,
                                transitions: updatedTransitions,
                              });
                            }}
                            placeholder="この遷移の詳細説明（どのような場合にこの遷移が発生するかなど）"
                          />
                        </Field>
                        <Field>
                          <Label>ステータス</Label>
                          <Select
                            value={
                              itemDetails.transitions[activeTransitionTab]
                                .status || "approved"
                            }
                            onChange={(e) => {
                              const updatedTransitions = [
                                ...itemDetails.transitions,
                              ];
                              updatedTransitions[activeTransitionTab] = {
                                ...updatedTransitions[activeTransitionTab],
                                status: e.target.value as ReviewStatus,
                              };
                              setItemDetails({
                                ...itemDetails,
                                transitions: updatedTransitions,
                              });
                            }}
                          >
                            {(Object.keys(STATUS_LABELS) as ReviewStatus[]).map(
                              (status) => (
                                <option key={status} value={status}>
                                  {STATUS_LABELS[status]}
                                </option>
                              )
                            )}
                          </Select>
                        </Field>
                      </Grid>
                    </TabPanel>
                  )}
                </TabContainer>
              ) : (
                <div
                  style={{
                    padding: "24px",
                    textAlign: "center",
                    border: "1px dashed #d1d5db",
                    borderRadius: "8px",
                    background: "#f9fafb",
                  }}
                >
                  <p
                    style={{
                      color: "#6b7280",
                      marginBottom: "12px",
                      fontSize: "0.875rem",
                    }}
                  >
                    画面遷移が設定されていません
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      const defaultServiceId = getOwnerServiceId();
                      const newTransition = {
                        targetId: "",
                        condition: "",
                        description: "",
                        transitionType: "same_tab" as ItemTransitionType,
                      };
                      setItemDetails({
                        ...itemDetails,
                        transitions: [newTransition],
                      });
                      setTransitionServiceSelections([defaultServiceId]);
                      setActiveTransitionTab(0);
                    }}
                    style={{ padding: "6px 16px", fontSize: "0.875rem" }}
                  >
                    + 最初の遷移を追加
                  </Button>
                </div>
              )}
            </BehaviorSection>
          </div>
        ) : (
          testTarget?.type === "item" &&
          as_item_details(testTarget?.params) &&
          (() => {
            const itemParams = as_item_details(testTarget.params)!;
            const viewFlow = parseInternalProcessFlow(
              itemParams.internalProcessFlow ??
                (itemParams as { internal_process_flow?: unknown })
                  .internal_process_flow
            );
            type ItemTransitionRow = NonNullable<
              ItemDetails["transitions"]
            >[number];
            const rawTransitions = itemParams.transitions;
            const viewTransitions: ItemTransitionRow[] =
              rawTransitions === undefined || rawTransitions === null
                ? []
                : Array.isArray(rawTransitions)
                  ? rawTransitions
                  : [rawTransitions as ItemTransitionRow];
            return (
              <div>
                <BehaviorSection>
                  <SectionTitleRow>
                    <BehaviorSubsectionTitle>I/O 分類</BehaviorSubsectionTitle>
                  </SectionTitleRow>
                  <InfoRow>
                    <InfoLabel>I/O</InfoLabel>
                    <InfoValue>
                      {itemParams.io ? (
                        <Chip variant="primary">
                          {ITEM_IO_LABEL_BY_VALUE[itemParams.io] ?? itemParams.io}
                        </Chip>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>未指定</span>
                      )}
                    </InfoValue>
                  </InfoRow>
                </BehaviorSection>

                <BehaviorSection>
                  <SectionTitleRow>
                    <BehaviorSubsectionTitle>DB マッピング</BehaviorSubsectionTitle>
                  </SectionTitleRow>
                  {(() => {
                    const completeRows = Array.isArray(itemParams.dbMapping)
                      ? itemParams.dbMapping.filter(
                          (m) =>
                            String(m?.table ?? "").trim() !== "" &&
                            String(m?.column ?? "").trim() !== ""
                        )
                      : [];
                    if (completeRows.length === 0) {
                      return (
                        <InfoRow>
                          <InfoLabel>マッピング</InfoLabel>
                          <InfoValue>未指定</InfoValue>
                        </InfoRow>
                      );
                    }
                    return (
                      <ChipContainer>
                        {completeRows.map((m, i) => (
                          <Chip key={i} variant="secondary">
                            {`${m.table}.${m.column}`}
                          </Chip>
                        ))}
                      </ChipContainer>
                    );
                  })()}
                </BehaviorSection>

                <BehaviorSection>
                  <SectionTitleRow>
                    <BehaviorSubsectionTitle>内部処理</BehaviorSubsectionTitle>
                    <FieldHelpButton
                      help={FIELD_HELP.internalProcess}
                      ariaLabel="内部処理の説明"
                    />
                  </SectionTitleRow>
                  {viewFlow.length === 0 ? (
                    <InfoRow>
                      <InfoLabel>フロー</InfoLabel>
                      <InfoValue>未登録</InfoValue>
                    </InfoRow>
                  ) : (
                    <FlowTimeline>
                      {viewFlow.map((entry, idx) => {
                        if (entry.kind === "parallel_start") {
                          return (
                            <FlowParallelMarker
                              key={entry.id}
                              $variant="start"
                            >
                              並列グループ開始
                            </FlowParallelMarker>
                          );
                        }
                        if (entry.kind === "parallel_end") {
                          return (
                            <FlowParallelMarker key={entry.id} $variant="end">
                              並列グループ終了（ここまで待機）
                            </FlowParallelMarker>
                          );
                        }
                        return (
                          <FlowStepCard key={entry.id}>
                            <FlowStepMeta>
                              <span>
                                処理 {idx + 1}
                                {entry.processName
                                  ? ` · ${entry.processName}`
                                  : ""}
                              </span>
                              {entry.isAsync && (
                                <Chip variant="warning">非同期</Chip>
                              )}
                            </FlowStepMeta>
                            {entry.processName ? (
                              <TransitionDetail>
                                処理名: {entry.processName}
                              </TransitionDetail>
                            ) : null}
                            {entry.processContent ? (
                              <TransitionDetail>
                                処理内容: {entry.processContent}
                              </TransitionDetail>
                            ) : null}
                            {entry.expectedChangeOnSuccess ? (
                              <TransitionDetail>
                                正常終了時:{" "}
                                {entry.expectedChangeOnSuccess}
                              </TransitionDetail>
                            ) : null}
                            {entry.expectedChangeOnError ? (
                              <TransitionDetail>
                                エラー発生時:{" "}
                                {entry.expectedChangeOnError}
                              </TransitionDetail>
                            ) : null}
                            {entry.precondition ? (
                              <TransitionDetail>
                                実行条件（前提）: {entry.precondition}
                              </TransitionDetail>
                            ) : null}
                          </FlowStepCard>
                        );
                      })}
                    </FlowTimeline>
                  )}
                </BehaviorSection>

                <BehaviorSection>
                  <SectionTitleRow>
                    <BehaviorSubsectionTitle>遷移</BehaviorSubsectionTitle>
                    <FieldHelpButton
                      help={FIELD_HELP.transitionSection}
                      ariaLabel="遷移の説明"
                    />
                  </SectionTitleRow>
                  {viewTransitions.length > 0 ? (
                    <InfoRow>
                      <InfoLabel>画面遷移</InfoLabel>
                      <InfoValue>
                        {viewTransitions.map((transition, idx) => (
                          <TransitionInfo
                            key={idx}
                            style={{
                              marginBottom:
                                idx < viewTransitions.length - 1 ? "12px" : 0,
                            }}
                          >
                            <TransitionHeader>
                              <Chip variant="primary">遷移</Chip>
                              <TransitionTarget>
                                →{" "}
                                {getTransitionTargetName(
                                  String(transition.targetId)
                                )}
                              </TransitionTarget>
                              {transition.status && (
                                <StatusBadge
                                  status={transition.status as ReviewStatus}
                                >
                                  {STATUS_LABELS[
                                    transition.status as ReviewStatus
                                  ] || STATUS_LABELS.approved}
                                </StatusBadge>
                              )}
                            </TransitionHeader>
                            <TransitionDetail>
                              遷移タイプ:{" "}
                              {ITEM_TRANSITION_TYPE_LABELS[
                                normalizeItemTransitionType(
                                  transition.transitionType ??
                                    (
                                      transition as {
                                        transition_type?: unknown;
                                      }
                                    ).transition_type
                                )
                              ]}
                            </TransitionDetail>
                            {transition.condition && (
                              <TransitionDetail>
                                条件:{" "}
                                <CodeValue>
                                  {String(transition.condition)}
                                </CodeValue>
                              </TransitionDetail>
                            )}
                            {transition.description && (
                              <TransitionDetail>
                                説明: {String(transition.description)}
                              </TransitionDetail>
                            )}
                          </TransitionInfo>
                        ))}
                      </InfoValue>
                    </InfoRow>
                  ) : (
                    <InfoRow>
                      <InfoLabel>画面遷移</InfoLabel>
                      <InfoValue>未登録</InfoValue>
                    </InfoRow>
                  )}
                </BehaviorSection>
              </div>
            );
          })()
        )}
      </TabContent>
    );
  };

  if (!open) return null;

  const maskEligible =
    editedData.type === "item" ||
    editedData.type === "section" ||
    editedData.type === "overlay";

  const availableTabs = [
    { id: "basic", label: "基本情報" },
    { id: "itemDetails", label: "項目詳細" },
    { id: "behaviorDetails", label: "動作詳細" },
    { id: "executionControl", label: "実行制御" },
    ...(maskEligible
      ? [{ id: "maskSettings", label: "マスク設定" }]
      : []),
    { id: "relatedTestCases", label: "関連テストケース" },
    { id: "metaInfo", label: "メタ情報" },
  ];

  return createPortal(
    <>
      <Overlay
        open={open}
        zIndex={zIndex}
        insetTopPx={backdropInsetTopPx}
        onClick={(e) => e.target === e.currentTarget && !isEditMode && onClose()}
      >
        <Modal
          onClick={(e) => e.stopPropagation()}
          data-testid={
            isEditMode ? "test-target-detail-edit" : "test-target-detail-view"
          }
        >
          <Header>
            <TitleSection>
              <Title>{getDialogTitle()}</Title>
              {testTarget && !isEditMode && (
                <TypeBadge type={testTarget.type}>
                  {getTypeLabel(testTarget.type)}
                </TypeBadge>
              )}
            </TitleSection>
            <HeaderActions>
              {!isEditMode && !hideEditButton && (
                <IconButton
                  onClick={() => {
                    // 編集モードに移行する際に現在の状態を保存
                    setOriginalTestTargetData({
                      editedData: JSON.parse(JSON.stringify(editedData)),
                      itemDetails: JSON.parse(JSON.stringify(itemDetails)),
                      selectorPairs: JSON.parse(JSON.stringify(selectorPairs)),
                      executionControl: JSON.parse(
                        JSON.stringify(executionControl)
                      ),
                      options: JSON.parse(JSON.stringify(options)),
                      transitionServiceSelections: JSON.parse(
                        JSON.stringify(transitionServiceSelections)
                      ),
                      selectedParentServiceId,
                      selectedParentViewId,
                      selectedParentItemId,
                      maskType,
                    });
                    setIsEditMode(true);
                  }}
                  title="編集"
                >
                  <EditIcon />
                </IconButton>
              )}
              {!isEditMode &&
                !hideEditButton &&
                testTarget?.type === "service" &&
                Boolean(testTarget?.id) &&
                onDelete && (
                  <IconButton
                    type="button"
                    onClick={() => setServiceDeleteDialogOpen(true)}
                    title="サービスを削除"
                    aria-label="サービスを削除"
                    style={{ color: "#b91c1c" }}
                  >
                    <TrashIcon />
                  </IconButton>
                )}
              {!isEditMode && (
                <IconButton onClick={onClose} title="閉じる">
                  <CloseIcon />
                </IconButton>
              )}
            </HeaderActions>
          </Header>

          <MainTabContainer>
            {availableTabs.map((tab) => (
              <Tab
                key={tab.id}
                $active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </Tab>
            ))}
          </MainTabContainer>

          <Content>
            {activeTab === "basic" && renderBasicTab()}
            {activeTab === "itemDetails" && renderItemDetailsTab()}
            {activeTab === "behaviorDetails" && renderBehaviorDetailsTab()}
            {activeTab === "executionControl" && renderExecutionControlTab()}
            {activeTab === "maskSettings" && renderMaskSettingsTab()}
            {activeTab === "relatedTestCases" && renderRelatedTestCasesTab()}
            {activeTab === "metaInfo" && renderMetaInfoTab()}
          </Content>

          {isEditMode && (
            <Footer>
              {footerValidationErrors.length > 0 && (
                <ValidationErrorContainer>
                  <ValidationErrorList>
                    {footerValidationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ValidationErrorList>
                </ValidationErrorContainer>
              )}
              <FooterActions>
                {hasChanges() && (
                  <ChangeWarningText>
                    ⚠️内容が変更されています
                  </ChangeWarningText>
                )}
                <Button onClick={handleCancel}>
                  <CancelIcon />
                  キャンセル
                </Button>
                <Button
                  onClick={handleSave}
                  variant="primary"
                  disabled={isSaving || !!nameLengthError}
                >
                  {isSaving ? (
                    <>
                      <CircularProgress size={16} style={{ marginRight: 8 }} />
                      保存中...
                    </>
                  ) : (
                    <>
                      <SaveIcon />
                      保存
                    </>
                  )}
                </Button>
              </FooterActions>
            </Footer>
          )}
        </Modal>
      </Overlay>
      <Dialog
        open={serviceDeleteDialogOpen}
        disableEscapeKeyDown={isDeletingService}
        onClose={() => {
          if (isDeletingService) return;
          setServiceDeleteDialogOpen(false);
        }}
        sx={{ zIndex: (zIndex ?? 1400) + 50 }}
      >
        <DialogTitle sx={{ color: "#b91c1c", fontWeight: 700 }}>
          サービスを削除しますか？
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            component="div"
            sx={{ color: "#374151", lineHeight: 1.75 }}
          >
            <p style={{ margin: "0 0 12px 0" }}>
              次のサービスを<strong>完全に削除</strong>しようとしています。
            </p>
            <div
              style={{
                margin: "0 0 12px 0",
                padding: "10px 12px",
                background: "#f9fafb",
                borderRadius: 8,
                fontWeight: 600,
                wordBreak: "break-word",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                {serviceDeleteConfirmationDisplayLabel(testTarget?.name)}
              </span>
              <Tooltip title="確認入力用にサービス名をコピー">
                <MuiIconButton
                  type="button"
                  size="small"
                  onClick={() => void handleCopyServiceNameForDeleteConfirm()}
                  aria-label="サービス名をコピー"
                >
                  <ContentCopyIcon fontSize="small" />
                </MuiIconButton>
              </Tooltip>
            </div>
            <p style={{ margin: "12px 0 0 0" }}>
              この操作は取り消せません。配下の画面・コンポーネント・項目などのテストターゲットや、関連する運用データへ与える影響を、チームで十分に確認してください。API
              側の仕様によっては、子孫が連鎖削除される場合があります。
            </p>
            <p style={{ margin: "12px 0 0 0", fontWeight: 600 }}>
              確認のため、削除するサービス名を入力してください
            </p>
            <TextField
              fullWidth
              margin="dense"
              label="削除するサービス名を入力"
              value={serviceDeleteConfirmInput}
              onChange={(e) => setServiceDeleteConfirmInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter" || isDeletingService) return;
                if (
                  !isServiceDeleteNameConfirmed(
                    serviceDeleteConfirmInput,
                    testTarget?.name
                  )
                ) {
                  return;
                }
                e.preventDefault();
                void handleConfirmServiceDelete();
              }}
              disabled={isDeletingService}
              autoComplete="off"
              spellCheck={false}
              sx={{ mt: 1, mb: 1 }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiConfirmButton
            onClick={() => setServiceDeleteDialogOpen(false)}
            disabled={isDeletingService}
          >
            キャンセル
          </MuiConfirmButton>
          <MuiConfirmButton
            color="error"
            variant="contained"
            disabled={
              isDeletingService ||
              !isServiceDeleteNameConfirmed(
                serviceDeleteConfirmInput,
                testTarget?.name
              )
            }
            onClick={() => void handleConfirmServiceDelete()}
          >
            {isDeletingService ? "削除中…" : "削除する"}
          </MuiConfirmButton>
        </DialogActions>
      </Dialog>
    </>,
    document.body
  );
};

export default TestTargetDetailView;
